import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView, animate } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Check, X, Sparkles, ChefHat, Package, Wallet,
  Users, Trophy, Cloud, MessageSquare, Palette, ShieldCheck, ScanLine,
  Zap, BarChart3, AlertTriangle, ClipboardCheck, Calendar, FileText,
  Briefcase, GraduationCap, ListChecks, Award, Lightbulb, Image as ImageIcon,
  ChevronDown, Loader2, Mail,
} from 'lucide-react';
import theme from '../lib/theme';
import ResultsTensionGate from '../components/ResultsTensionGate';
import RoiCalculator from '../components/RoiCalculator';
import AppDemoVideo from '../components/AppDemoVideo';
import AppFeaturesGrid from '../components/AppFeaturesGrid';
import Resto360Wordmark from '../components/Resto360Wordmark';
import Resto360Mark from '../components/Resto360Mark';
import RealDashboardShowcase from '../components/RealDashboardShowcase';
import OffersQuickAccess from '../components/OffersQuickAccess';
import SabrilPixarAvatar from '../components/SabrilPixarAvatar';
import SabrilCloserBubble from '../components/SabrilCloserBubble';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = 'https://app.audit-resto.fr';
const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/3cI5kw7M270gaT7dSeg7e03';
const PRICE = 229;
const PRICE_PER_EXTRA_RESTO = 99;

// Real screenshots of the live app
const SHOT_RECETTES = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/2r1igojt_Capture%20d%E2%80%99e%CC%81cran%202026-05-15%20a%CC%80%2011.44.15.png';
const SHOT_SIDEBAR_1 = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/r94zajyd_Capture%20d%E2%80%99e%CC%81cran%202026-05-15%20a%CC%80%2011.44.31.png';
const SHOT_SIDEBAR_2 = 'https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/a3h5hp3p_Capture%20d%E2%80%99e%CC%81cran%202026-05-15%20a%CC%80%2011.44.42.png';

// ============================== UTILITIES ==============================

const Counter = ({ to, suffix = '', prefix = '', duration = 1.4, className, style }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: 'easeOut',
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, duration]);
  return (
    <span ref={ref} className={className} style={style}>
      {prefix}{val.toLocaleString('fr-FR')}{suffix}
    </span>
  );
};

const SectionTag = ({ children }) => (
  <p className="text-[11px] font-bold tracking-[3px] uppercase mb-3" style={{ color: theme.orange }}>
    {children}
  </p>
);

const SectionTitle = ({ children, className = '' }) => (
  <h2
    className={`font-bebas leading-[0.94] tracking-tight ${className}`}
    style={{ color: theme.text, fontSize: 'clamp(36px,6vw,72px)' }}
  >
    {children}
  </h2>
);

// ============================== HUB CARD ==============================

// Compact mini-mockup previews shown inside each HubCard
const HUB_PREVIEWS = {
  cuisine: ({ accent }) => {
    const txt = accent ? '#fff' : '#1a1a1a';
    const sub = accent ? 'rgba(255,255,255,0.6)' : 'rgba(26,26,26,0.55)';
    const bg = accent ? 'rgba(255,255,255,0.06)' : 'rgba(26,26,26,0.03)';
    const rows = [
      { n: 'Avo Bali', fc: 24, c: '#22C55E' },
      { n: 'Tartare', fc: 28, c: '#22C55E' },
      { n: 'Burger', fc: 33, c: '#F59E0B' },
    ];
    return (
      <div className="rounded-xl p-3 space-y-1.5" style={{ backgroundColor: bg }}>
        <p className="text-[9px] font-bold tracking-[1.5px] uppercase" style={{ color: sub }}>Food cost · Top 3 plats</p>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[11px] font-semibold flex-1 truncate" style={{ color: txt }}>{r.n}</span>
            <div className="w-12 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(26,26,26,0.12)' }}>
              <div className="h-full" style={{ width: `${r.fc * 2}%`, backgroundColor: r.c }} />
            </div>
            <span className="font-bebas text-sm tabular-nums leading-none w-8 text-right" style={{ color: r.c }}>{r.fc}%</span>
          </div>
        ))}
      </div>
    );
  },
  fournisseurs: () => (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.18)' }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: '#8B5CF6', color: '#fff' }}>
          <FileText className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold leading-tight" style={{ color: '#1a1a1a' }}>Metro · Commande hebdo</p>
          <p className="text-[9px]" style={{ color: 'rgba(26,26,26,0.55)' }}>18 produits · auto 09:00</p>
        </div>
        <span className="font-bebas text-base leading-none tabular-nums" style={{ color: '#8B5CF6' }}>347€</span>
      </div>
      <div className="flex gap-1">
        {[80, 60, 90, 75, 55, 70].map((h, i) => (
          <div key={i} className="flex-1 rounded-sm" style={{ height: `${h / 8}px`, backgroundColor: 'rgba(139,92,246,0.35)' }} />
        ))}
      </div>
    </div>
  ),
  finance: () => (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.18)' }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[9px] font-bold tracking-[1.5px] uppercase" style={{ color: 'rgba(26,26,26,0.55)' }}>Prime Cost</p>
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#22C55E', color: '#fff' }}>EXCELLENT</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-bebas leading-none tabular-nums" style={{ color: '#16A34A', fontSize: 34 }}>58.4</span>
        <span className="font-bebas text-base" style={{ color: '#16A34A' }}>%</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <div>
          <p className="text-[9px]" style={{ color: 'rgba(26,26,26,0.55)' }}>Food cost</p>
          <p className="font-bebas text-base leading-none tabular-nums" style={{ color: '#1a1a1a' }}>27.8%</p>
        </div>
        <div>
          <p className="text-[9px]" style={{ color: 'rgba(26,26,26,0.55)' }}>Labor</p>
          <p className="font-bebas text-base leading-none tabular-nums" style={{ color: '#1a1a1a' }}>30.6%</p>
        </div>
      </div>
    </div>
  ),
  rh: () => (
    <div className="rounded-xl p-3 space-y-1.5" style={{ backgroundColor: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.16)' }}>
      <p className="text-[9px] font-bold tracking-[1.5px] uppercase" style={{ color: 'rgba(26,26,26,0.55)' }}>Planning suggéré · IA Sophie</p>
      {[
        { n: 'Marie L.', t: '11h–23h', c: '#F05A1A' },
        { n: 'Lucas P.', t: '10h–22h', c: '#3B82F6' },
        { n: 'Sophie M.', t: '11h–23h', c: '#EC4899' },
      ].map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0" style={{ backgroundColor: p.c }}>
            {p.n.split(' ').map(x => x[0]).join('')}
          </div>
          <span className="text-[11px] font-semibold flex-1 truncate" style={{ color: '#1a1a1a' }}>{p.n}</span>
          <span className="text-[10px] font-bold tabular-nums" style={{ color: 'rgba(26,26,26,0.6)' }}>{p.t}</span>
          <Check className="w-3 h-3" style={{ color: '#22C55E' }} strokeWidth={3} />
        </div>
      ))}
    </div>
  ),
  performance: () => (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
      <p className="text-[9px] font-bold tracking-[1.5px] uppercase mb-2" style={{ color: 'rgba(26,26,26,0.55)' }}>Classement Mai · Primes auto</p>
      <div className="space-y-1.5">
        {[
          { r: '🥇', n: 'Marie L.', s: 96, p: '250€' },
          { r: '🥈', n: 'Sophie M.', s: 88, p: '200€' },
          { r: '🥉', n: 'Lucas P.', s: 82, p: '150€' },
        ].map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-sm w-5 text-center">{p.r}</span>
            <span className="text-[11px] font-semibold flex-1 truncate" style={{ color: '#1a1a1a' }}>{p.n}</span>
            <span className="font-bebas text-sm leading-none tabular-nums w-6 text-right" style={{ color: '#1a1a1a' }}>{p.s}</span>
            <span className="font-bebas text-sm leading-none tabular-nums w-10 text-right" style={{ color: '#F59E0B' }}>{p.p}</span>
          </div>
        ))}
      </div>
    </div>
  ),
  marketing: () => (
    <div className="rounded-xl p-3 text-white" style={{ background: 'linear-gradient(135deg,#EC4899 0%,#8B5CF6 100%)' }}>
      <p className="text-[9px] font-bold tracking-[1.5px] uppercase opacity-85 mb-2">@maisonbensimon · Clara IA</p>
      <div className="grid grid-cols-3 gap-1.5">
        {[
          { v: '4 280', l: 'Followers', up: '+12%' },
          { v: '184k', l: 'Portée 30j', up: '+38%' },
          { v: '5.8%', l: 'Eng.', up: '+1.4pt' },
        ].map((s, i) => (
          <div key={i} className="rounded-md p-1.5 text-center" style={{ backgroundColor: 'rgba(0,0,0,0.22)' }}>
            <p className="font-bebas text-sm leading-none tabular-nums">{s.v}</p>
            <p className="text-[8px] opacity-80 mt-0.5 leading-tight">{s.l}</p>
            <p className="text-[8px] font-bold mt-0.5" style={{ color: '#86EFAC' }}>↑ {s.up}</p>
          </div>
        ))}
      </div>
    </div>
  ),
};

const HubCard = ({ icon: Icon, color, title, count, modules, accent = false, previewKey, delay = 0 }) => {
  const Preview = previewKey ? HUB_PREVIEWS[previewKey] : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay }}
      className="rounded-3xl p-6 sm:p-7 flex flex-col"
      style={{
        backgroundColor: accent ? theme.text : theme.card,
        border: `1px solid ${accent ? theme.text : theme.border}`,
        boxShadow: accent ? '0 10px 30px rgba(0,0,0,0.18)' : theme.shadow,
      }}
      data-testid={`hub-card-${title.toLowerCase().replace(/\s/g, '-')}`}
      id={previewKey ? `hub-${previewKey}` : undefined}
    >
      <div className="flex items-start justify-between mb-5">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: accent ? 'rgba(240,90,26,0.18)' : `${color}18` }}
        >
          <Icon className="w-6 h-6" style={{ color: accent ? theme.orange : color }} />
        </div>
        <span
          className="text-[10px] font-bold tracking-[2px] uppercase px-2.5 py-1 rounded-full"
          style={{
            color: accent ? theme.bg : theme.textMuted,
            backgroundColor: accent ? 'rgba(255,255,255,0.12)' : 'rgba(26,26,26,0.05)',
          }}
        >
          {count} modules
        </span>
      </div>

      {Preview && (
        <div className="mb-5">
          <Preview accent={accent} />
        </div>
      )}

      <h3
        className="font-bebas text-3xl sm:text-4xl leading-none mb-5"
        style={{ color: accent ? theme.bg : theme.text }}
      >
        {title}
      </h3>
      <ul className="space-y-3">
        {modules.map((m, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm leading-snug">
            <Check
              className="w-4 h-4 flex-shrink-0 mt-0.5"
              style={{ color: accent ? theme.orange : color }}
              strokeWidth={3}
            />
            <div className="flex-1 min-w-0">
              <span style={{ color: accent ? 'rgba(245,243,240,0.85)' : theme.text }}>
                <strong>{m.name}</strong>
                {m.detail && (
                  <span style={{ color: accent ? 'rgba(245,243,240,0.55)' : theme.textMuted }}>
                    {' '}— {m.detail}
                  </span>
                )}
              </span>
              {(m.loss || m.unlock) && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {m.loss && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md"
                      style={{
                        backgroundColor: accent ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.10)',
                        color: accent ? '#FCA5A5' : '#DC2626',
                        border: accent ? '1px solid rgba(239,68,68,0.35)' : '1px solid rgba(239,68,68,0.25)',
                      }}
                    >
                      🩸 Sans ça : {m.loss}
                    </span>
                  )}
                  {m.unlock && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md"
                      style={{
                        backgroundColor: accent ? 'rgba(34,197,94,0.18)' : 'rgba(34,197,94,0.10)',
                        color: accent ? '#86EFAC' : '#059669',
                        border: accent ? '1px solid rgba(34,197,94,0.35)' : '1px solid rgba(34,197,94,0.25)',
                      }}
                    >
                      🚀 Avec ça : {m.unlock}
                    </span>
                  )}
                </div>
              )}
            </div>
          </li>
      ))}
      </ul>
    </motion.div>
  );
};

// ============================== KILLER FEATURE CARD ==============================

const KillerCard = ({ icon: Icon, badge, title, desc, image, mock, reverse = false, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.6, delay }}
    className={`grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 items-center ${reverse ? 'md:[direction:rtl]' : ''}`}
  >
    <div className="md:[direction:ltr]">
      <span
        className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[2px] uppercase px-3 py-1 rounded-full mb-4"
        style={{ backgroundColor: theme.orangeDim, color: theme.orange, border: `1px solid ${theme.orangeBorder}` }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge}
      </span>
      <h3
        className="font-bebas text-3xl sm:text-5xl leading-[0.95] mb-4"
        style={{ color: theme.text }}
      >
        {title}
      </h3>
      <p className="text-base sm:text-lg leading-relaxed" style={{ color: theme.textMuted }}>
        {desc}
      </p>
    </div>
    <div className="md:[direction:ltr]">
      {mock ? (
        mock
      ) : (
        <div
          className="rounded-3xl overflow-hidden"
          style={{ border: `1px solid ${theme.border}`, boxShadow: theme.shadowLg, backgroundColor: theme.card }}
        >
          <img src={image} alt={title} className="w-full h-auto block" loading="lazy" />
        </div>
      )}
    </div>
  </motion.div>
);

// ============================== POINTAGE PHONE MOCKUP ==============================

const PointageMock = () => (
  <div className="flex justify-center">
    <div
      className="relative rounded-[44px] p-3 max-w-[300px] w-full"
      style={{
        background: 'linear-gradient(135deg,#1a1a1a 0%,#2a2a2a 100%)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
    >
      {/* Notch */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 rounded-b-2xl z-10" style={{ backgroundColor: '#1a1a1a' }} />
      <div className="rounded-[34px] overflow-hidden" style={{ backgroundColor: '#0F0F0F', minHeight: 520 }}>
        {/* Status bar */}
        <div className="flex items-center justify-between px-6 pt-3 text-[10px] font-bold text-white/80">
          <span>11:02</span>
          <span>5G ●●●●</span>
        </div>

        {/* Title */}
        <div className="px-5 pt-7 pb-4 text-center">
          <p className="text-[10px] font-bold tracking-[2px] uppercase text-white/50 mb-1">Pointage du jour</p>
          <h4 className="font-bebas text-2xl text-white">Vendredi 15 mai</h4>
        </div>

        {/* Selfie preview */}
        <div className="px-5">
          <div
            className="relative rounded-2xl overflow-hidden mx-auto"
            style={{
              aspectRatio: '3/4',
              maxWidth: 200,
              background: 'linear-gradient(180deg,#3a2a1f 0%,#1a1410 100%)',
            }}
          >
            {/* Avatar silhouette */}
            <div className="absolute inset-0 flex items-end justify-center pb-6">
              <div
                className="w-24 h-24 rounded-full -mb-4"
                style={{ background: 'radial-gradient(circle, rgba(240,90,26,0.4) 0%, rgba(240,90,26,0.05) 70%)' }}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl">👨‍🍳</div>
            </div>
            {/* Polo orange overlay */}
            <div
              className="absolute bottom-0 left-0 right-0 h-20"
              style={{ background: 'linear-gradient(180deg, transparent 0%, #F05A1A 100%)' }}
            />
            {/* "Polo détecté" badge */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="absolute bottom-2 left-2 right-2 text-center"
            >
              <span
                className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full"
                style={{ backgroundColor: 'rgba(34,197,94,0.95)', color: '#fff' }}
              >
                <Check className="w-3 h-3" strokeWidth={3} />
                Polo détecté
              </span>
            </motion.div>
            {/* Scan corners */}
            {['top-3 left-3', 'top-3 right-3', 'bottom-3 left-3', 'bottom-3 right-3'].map((pos, i) => (
              <div key={i} className={`absolute ${pos} w-5 h-5`}>
                <div className="w-full h-1 bg-white/60" />
                <div className="w-1 h-full bg-white/60 -mt-1" />
              </div>
            ))}
          </div>
        </div>

        {/* Verification rows */}
        <div className="px-5 py-5 space-y-2.5">
          {[
            { label: 'Selfie pris', sub: '11:02:14 · Caméra avant', ok: true },
            { label: 'Polo de l\'établissement', sub: 'Couleur orange détectée · 98%', ok: true },
            { label: 'GPS · sur place', sub: '12 rue Mallet · Paris 11e', ok: true },
          ].map((r, i) => (
            <motion.div
              key={i}
              initial={{ x: -20, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 + i * 0.15 }}
              className="flex items-center gap-3 p-2.5 rounded-xl"
              style={{ backgroundColor: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)' }}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#22C55E' }}
              >
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">{r.label}</p>
                <p className="text-[10px] text-white/60 truncate">{r.sub}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA button */}
        <div className="px-5 pb-6">
          <div
            className="w-full text-center py-3.5 rounded-2xl font-extrabold text-sm"
            style={{ background: 'linear-gradient(135deg,#F05A1A,#D94D0F)', color: '#fff', boxShadow: '0 8px 20px rgba(240,90,26,0.4)' }}
          >
            ✓ Pointage validé · Lucas P.
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================== COACH IA CHAT MOCKUP (LIVE TYPING) ==============================

const TypingDots = () => (
  <div className="flex items-center gap-1 px-4 py-3 rounded-2xl rounded-tl-md w-fit"
       style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ backgroundColor: 'rgba(255,255,255,0.65)' }}
        animate={{ y: [0, -4, 0], opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
      />
    ))}
  </div>
);

const CoachChatMock = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [step, setStep] = useState(0); // 0 idle · 1 user msg · 2 typing · 3 reply · 4 chips

  useEffect(() => {
    if (!inView) return;
    const timers = [
      setTimeout(() => setStep(1), 350),
      setTimeout(() => setStep(2), 1300),
      setTimeout(() => setStep(3), 3300),
      setTimeout(() => setStep(4), 4500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [inView]);

  return (
    <div
      ref={ref}
      className="rounded-3xl p-5 sm:p-6"
      style={{
        backgroundColor: '#1A1A1A',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 mb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" style={{ border: '2px solid #F59E0B' }}>
          <img src="/agents/sabril.png" alt="Sabril IA" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bebas text-xl text-white leading-none">SABRIL · COACH IA</p>
          <p className="text-[11px] mt-0.5" style={{ color: '#86EFAC' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 animate-pulse" style={{ backgroundColor: '#22C55E' }} />
            {step >= 2 && step < 3 ? 'écrit…' : 'en ligne · répond en 3s'}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="space-y-3 min-h-[440px]">
        <AnimatePresence>
          {step >= 1 && (
            <motion.div
              key="user-msg"
              initial={{ x: 20, opacity: 0, scale: 0.96 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring', damping: 22 }}
              className="flex justify-end"
            >
              <div className="max-w-[80%] rounded-2xl rounded-tr-md px-4 py-3" style={{ backgroundColor: theme.orange }}>
                <p className="text-sm text-white leading-snug">
                  Mon food cost est à 38%, j'ai besoin d'embaucher un commis. Je fais quoi en premier ?
                </p>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="typing"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-start"
            >
              <TypingDots />
            </motion.div>
          )}

          {step >= 3 && (
            <motion.div
              key="reply"
              initial={{ x: -20, opacity: 0, scale: 0.96 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              transition={{ type: 'spring', damping: 22 }}
              className="flex justify-start"
            >
              <div
                className="max-w-[88%] rounded-2xl rounded-tl-md px-4 py-3"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.92)' }}>
                  Avant d'embaucher : <strong style={{ color: '#F59E0B' }}>baisse ton food cost d'abord</strong>.
                  <br />Sur 47k€ de CA, 38% → 30% = <strong style={{ color: '#86EFAC' }}>+3 760€/mois</strong> récupérés.
                </p>
                <p className="text-sm leading-relaxed mt-2" style={{ color: 'rgba(255,255,255,0.92)' }}>
                  <strong style={{ color: '#fff' }}>Plan d'action 14 jours :</strong>
                </p>
                <ul className="text-sm space-y-1.5 mt-2" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  <li>✅ Léo calibre tes 5 plats top (J1)</li>
                  <li>✅ Sophie réorganise le planning (J3)</li>
                  <li>✅ Tu négocies tes 3 fournisseurs (J7)</li>
                  <li>✅ Tu mesures · tu décides du commis (J14)</li>
                </ul>
                <p className="text-xs mt-3 italic" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Le commis se justifie quand tu génères plus de marge — pas l'inverse.
                </p>
              </div>
            </motion.div>
          )}

          {step >= 4 && (
            <motion.div
              key="chips"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex flex-wrap gap-2 pt-2"
            >
              {['🍳 Lance le plan Léo', '👥 Brief Sophie', '📞 Trame négo fournisseur'].map((c, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.12 }}
                  className="text-[11px] font-bold px-3 py-1.5 rounded-full"
                  style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#FCD34D', border: '1px solid rgba(245,158,11,0.3)' }}
                >
                  {c}
                </motion.span>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Input bar */}
      <div className="mt-5 pt-4 flex items-center gap-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div
          className="flex-1 rounded-full px-4 py-2.5 text-xs"
          style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }}
        >
          Pose ta question à Sabril…
        </div>
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: '#F59E0B' }}
        >
          <ArrowRight className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
};

// ============================== FAQ ==============================

const FaqItem = ({ q, a, isOpen, onClick, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.04 }}
    className="rounded-2xl overflow-hidden"
    style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onClick}
      className="w-full px-5 py-4 sm:px-6 sm:py-5 text-left flex items-center justify-between gap-4"
    >
      <span className="text-sm sm:text-base font-extrabold" style={{ color: theme.text }}>{q}</span>
      <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
        <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: theme.textMuted }} />
      </motion.div>
    </button>
    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        className="px-5 pb-5 sm:px-6 sm:pb-6"
      >
        <p className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>{a}</p>
      </motion.div>
    )}
  </motion.div>
);

// ============================== MAIN PAGE ==============================

const AppLaunchPage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [loading, setLoading] = useState(false);
  const [welcome, setWelcome] = useState(null); // { firstName, email } when subscription is confirmed
  const [liveTick, setLiveTick] = useState(0); // increments every 4s to animate pointage widget

  // Live tick for the Pointage widget — pretends "real-time" updates
  useEffect(() => {
    const id = setInterval(() => setLiveTick((t) => t + 1), 4000);
    return () => clearInterval(id);
  }, []);

  // Swap favicon + page title + OG meta tags to "360 Resto" branding while on /app, restore on unmount.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = '360 Resto — App OS pour restaurateurs · 229€/mois';

    const head = document.head;
    // Remove any existing favicon links so we control the icon while on /app
    const existing = Array.from(head.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]'));
    existing.forEach((el) => el.setAttribute('data-r360-hidden', 'true'));
    existing.forEach((el) => head.removeChild(el));

    const newIcon = document.createElement('link');
    newIcon.rel = 'icon';
    newIcon.type = 'image/svg+xml';
    newIcon.href = '/360-resto-mark.svg';
    newIcon.setAttribute('data-r360-swap', 'true');
    head.appendChild(newIcon);

    // Open Graph + Twitter Card meta tags — full branded preview on social shares
    const origin = window.location.origin;
    const ogImageUrl = `${origin}/og-360-resto.png`;
    const metaConfig = [
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: '360 Resto — L\'app OS qui remplace ton manager' },
      { property: 'og:description', content: '28 modules · 7 agents IA · 229€/mois · 15 jours d\'essai gratuit. Pilote finances, équipe, recettes, réseaux & livraisons depuis un seul écran.' },
      { property: 'og:image', content: ogImageUrl },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:url', content: `${origin}/app` },
      { property: 'og:site_name', content: '360 Resto' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: '360 Resto — L\'app OS qui remplace ton manager' },
      { name: 'twitter:description', content: '28 modules · 7 agents IA · 229€/mois · 15j gratuits' },
      { name: 'twitter:image', content: ogImageUrl },
    ];
    const addedMeta = [];
    metaConfig.forEach(({ property, name, content }) => {
      const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      // Hide any existing matching tags so we can restore them later
      const prev = head.querySelector(selector);
      if (prev) {
        prev.setAttribute('data-r360-hidden', 'true');
        head.removeChild(prev);
        addedMeta.push({ restore: prev });
      }
      const tag = document.createElement('meta');
      if (property) tag.setAttribute('property', property);
      if (name) tag.setAttribute('name', name);
      tag.setAttribute('content', content);
      tag.setAttribute('data-r360-swap', 'true');
      head.appendChild(tag);
      addedMeta.push({ added: tag });
    });

    return () => {
      document.title = previousTitle;
      // Restore favicon
      const swapped = head.querySelector('link[data-r360-swap="true"]');
      if (swapped) head.removeChild(swapped);
      existing.forEach((el) => head.appendChild(el));
      // Restore meta tags
      head.querySelectorAll('meta[data-r360-swap="true"]').forEach((el) => head.removeChild(el));
      addedMeta.forEach(({ restore }) => {
        if (restore) head.appendChild(restore);
      });
    };
  }, []);

  // Detect Stripe redirect → confirm subscription + show "Merci — va sur ta boîte mail" popup
  // (Account creation happens via the magic link in Sophie's welcome email)
  const [postCheckoutSuccess, setPostCheckoutSuccess] = useState(false);
  const [postCheckoutData, setPostCheckoutData] = useState(null);
  const [postCheckoutSessionId, setPostCheckoutSessionId] = useState(null);
  const [emailSentAt, setEmailSentAt] = useState(null); // timestamp ms when last email was (re-)sent
  const [nowTick, setNowTick] = useState(Date.now());   // ticks every 1s so the countdown re-renders
  const [resendState, setResendState] = useState({ loading: false, error: '', success: false });

  // 1s ticker — only runs while the popup is open
  useEffect(() => {
    if (!postCheckoutSuccess) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [postCheckoutSuccess]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('subscribed') !== '1' || !sessionId) return;

    // Show success popup immediately — no waiting, no auto-redirect
    setPostCheckoutSuccess(true);
    setPostCheckoutSessionId(sessionId);
    setEmailSentAt(Date.now()); // Sophie's welcome email is sent by the webhook/confirm at this moment
    let cancelled = false;

    (async () => {
      // Best-effort enrichment of the popup (first name / email / login url) — never blocks the UI
      for (let attempt = 0; attempt < 6 && !cancelled; attempt++) {
        try {
          const res = await fetch(`${API_URL}/api/app/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
          });
          const data = await res.json();
          if (cancelled) return;
          if (data?.paid) {
            setPostCheckoutData({
              firstName: data.first_name || '',
              restaurantName: data.restaurant_name || '',
              email: data.email || '',
              loginUrl: data.login_url || '',
            });
            if (data.login_url) return; // fully provisioned — stop polling
          }
        } catch {
          // swallow, retry
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
    })();

    // Clean URL so a refresh doesn't loop the modal
    window.history.replaceState({}, '', '/app');
    return () => { cancelled = true; };
  }, []);

  // Resend Sophie's welcome email (rate-limited backend-side: 1 / 30s)
  const handleResendEmail = async () => {
    if (!postCheckoutSessionId) return;
    setResendState({ loading: true, error: '', success: false });
    try {
      const res = await fetch(`${API_URL}/api/app/resend-welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: postCheckoutSessionId }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || 'Erreur lors du renvoi du mail');
      }
      setEmailSentAt(Date.now());
      setResendState({ loading: false, error: '', success: true });
      // Hide the success badge after 5s
      setTimeout(() => setResendState((s) => ({ ...s, success: false })), 5000);
    } catch (e) {
      setResendState({ loading: false, error: e.message || 'Erreur réseau', success: false });
    }
  };

  const [showRestaurantPrompt, setShowRestaurantPrompt] = useState(false);
  const [restaurantName, setRestaurantName] = useState(() => {
    try { return localStorage.getItem('app_restaurant_name') || ''; } catch (_) { return ''; }
  });
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  const handleSubscribe = () => {
    setSubscribeError('');
    setShowRestaurantPrompt(true);
  };

  const handleConfirmSubscribe = async () => {
    const name = (restaurantName || '').trim();
    if (name.length < 2) {
      setSubscribeError('Indique le nom de ton restaurant pour personnaliser ton espace.');
      return;
    }
    try { localStorage.setItem('app_restaurant_name', name); } catch (_) { /* ignore */ }
    setSubscribing(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          restaurant_name: name,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.url) {
        throw new Error(data?.detail || 'Création de la session échouée');
      }
      window.location.href = data.url;
    } catch (e) {
      setSubscribeError(e.message || 'Erreur réseau, réessaie.');
      setSubscribing(false);
    }
  };

  const hubs = [
    {
      icon: ChefHat,
      color: '#F05A1A',
      title: 'Cuisine & Stocks',
      count: 4,
      accent: true,
      previewKey: 'cuisine',
      modules: [
        { name: 'Recettes & Coûts (Léo IA)', detail: 'scan photo de fiche manuscrite → recette calibrée au gramme, food cost auto', loss: '−1 200€/mois (food cost dérive)', unlock: '+8% marge cuisine' },
        { name: 'Inventaire & Stocks', detail: 'photo des armoires OCR · ou vocal salarié · ou manuel — au choix', loss: '−600€/mois (vol + casse non vue)', unlock: 'Stock optimisé à 3 jours' },
        { name: 'DLC & Étiquettes HACCP', detail: 'rappels péremption + impression étiquettes traçabilité', loss: '−400€/mois (produits jetés)', unlock: '0% gaspillage matière' },
        { name: 'Checklists HACCP', detail: 'normes hygiène, traçabilité, audit prêt', loss: '−1 500€ (amende DDPP moyenne)', unlock: 'Compliance prouvée 100%' },
      ],
    },
    {
      icon: Package,
      color: '#8B5CF6',
      title: 'Fournisseurs',
      count: 4,
      previewKey: 'fournisseurs',
      modules: [
        { name: 'Liste produits', detail: 'tu rentres tes prix fournisseurs → food cost recalculé partout en direct', loss: '−300€/mois (hausses non détectées)', unlock: 'Prix sync sur 100% de la carte' },
        { name: 'Achats récurrents', detail: 'commandes hebdo en 2 clics', loss: '−2h/semaine (commandes manuelles)', unlock: 'Commandes en pilote auto' },
        { name: 'Commandes', detail: 'PDF auto envoyé fournisseur · pas de relance manuelle', loss: '−200€/mois (oublis + ruptures)', unlock: '100% commandes livrées à temps' },
        { name: 'Tickets Achats', detail: 'OCR factures, prix sync automatique sur les recettes', loss: '−5h/semaine (encodage compta)', unlock: '+450€/mois (marge re-négociable)' },
      ],
    },
    {
      icon: Wallet,
      color: '#22C55E',
      title: 'Finance',
      count: 3,
      previewKey: 'finance',
      modules: [
        { name: 'Rentabilité en direct (Théo IA)', detail: 'CA, marge, prime cost, EBE temps réel', loss: '−2 500€/mois (décisions à l\'aveugle)', unlock: 'Pilotage daily · EBE +6 pts' },
        { name: 'Suivi Caisse', detail: 'écarts détectés auto, fin de service nette', loss: '−400€/mois (écarts caisse non vus)', unlock: '0€ d\'écart fin de mois' },
        { name: 'Génération de factures', detail: 'factures clients · B2B · paie · auto-PDF + envoi mail', loss: '−4h/mois (admin manuel)', unlock: 'Factures envoyées en 1 clic' },
      ],
    },
    {
      icon: Users,
      color: '#3B82F6',
      title: 'Équipe & RH (Sophie IA)',
      count: 9,
      previewKey: 'rh',
      modules: [
        { name: 'Planning suggéré par IA', detail: 'Sophie te propose le planning optimal selon ton équipe & ton CA', loss: '−1 800€/mois (sur-staffing)', unlock: 'Masse salariale -4 pts' },
        { name: 'Pointage selfie + polo', detail: "vérification visuelle : t'es bien sur place, en tenue", loss: '−700€/mois (heures sup fictives)', unlock: '100% heures réelles facturées' },
        { name: 'Suivi des tâches', detail: "tâches IA assignées au planning · validation par photo", loss: '−5h/semaine (relance manuelle)', unlock: 'Tâches faites sans demander' },
        { name: 'Documents RH', detail: 'fiches de paie, attestations, signatures électroniques', loss: '−2h/mois (admin RH)', unlock: 'Onboarding RH en 5 min' },
        { name: 'Contrats RH', detail: 'CDI/CDD/extras générés en 1 minute', loss: '−150€/contrat (avocat / templates)', unlock: 'Contrats légaux en 1 clic' },
        { name: 'Congés', detail: 'demandes validées en 1 swipe', loss: '−3h/mois (Excel + emails)', unlock: 'Planning congés auto-synchronisé' },
        { name: 'Base Camp · Onboarding & Formations', detail: 'parcours d\'intégration structuré · montée en compétences · certificats HACCP officiels', loss: '−1 500€/recrue (chef qui forme 3 sem)', unlock: 'Nouveau opérationnel en 15j' },
        { name: 'Recrutement (Manu IA)', detail: 'scan CV → classement auto, scoring candidats', loss: '−4h/recrutement (tri CV manuel)', unlock: 'Top 3 candidats en 10 min' },
        { name: 'Équipe', detail: 'fiches profils, primes individuelles', loss: '−40% turnover (talent non identifié)', unlock: 'Talents fidélisés · plan carrière' },
      ],
    },
    {
      icon: Trophy,
      color: '#F59E0B',
      title: 'Performance & Litiges',
      count: 3,
      previewKey: 'performance',
      modules: [
        { name: 'Objectifs & Primes', detail: 'score par salarié : tâches validées, CA généré, assiduité', loss: '−2-3h/mois (calcul primes manuel)', unlock: 'Salariés qui bossent comme associés' },
        { name: 'Preuves Uber Eats / Deliveroo / Glovo', detail: 'photo + horodatage de chaque commande → litiges remboursés', loss: '−150 à 300€/mois (litiges acceptés)', unlock: '+1 800€/an récupérés' },
        { name: 'Insights IA', detail: 'anomalies détectées, recommandations terrain par Sabril', loss: '−1 000€/mois (problèmes non vus)', unlock: 'Alertes proactives · zéro angle mort' },
      ],
    },
    {
      icon: Sparkles,
      color: '#EC4899',
      title: 'Outils Marketing & IA',
      count: 6,
      previewKey: 'marketing',
      modules: [
        { name: 'Réseaux Sociaux (Clara IA)', detail: 'générateur vidéos virales + scripts Insta/TikTok', loss: '−15% CA non capté (concurrence visible)', unlock: '+4 500€/mois (15% CA digital)' },
        { name: 'Insights & Tendances 2026 (Nora IA)', detail: 'analyse ton domaine, te propose le menu gagnant selon ta zone', loss: '−10% CA non développé (concept dépassé)', unlock: 'Toujours 6 mois en avance sur la concurrence' },
        { name: 'Menu gagnant', detail: 'top plats à pousser ce mois selon météo, événements locaux, ticket moyen', loss: '−800€/mois (plats stars sous-poussés)', unlock: '+12% ticket moyen' },
        { name: 'Chat IA Coach Sabril', detail: 'mentor stratégique 24/7 — méthode Sabril 10 ans terrain', loss: '−500€/h (consulting externe)', unlock: 'Réponse en 30s, 24/7' },
        { name: 'Charte Graphique', detail: 'visuels, templates story prêts à publier', loss: '−800€/mois (graphiste freelance)', unlock: 'Identité pro · publications hebdo' },
        { name: 'Météo 15 jours', detail: 'anticipe les services, ajuste les commandes', loss: '−300€/semaine (sur-stock pluie)', unlock: 'Commandes calées sur la météo' },
      ],
    },
  ];

  const faqs = [
    {
      q: "C'est quoi exactement les 7 agents IA ?",
      a: "6 IA spécialisées : Clara (réseaux sociaux & vidéos virales), Sophie (RH & planning), Léo (cuisine, recettes & food cost), Manu (recrutement & évaluation), Théo (finance & primes) et Sabril IA (coach stratégique). Chacun bosse sur son domaine — pas un chatbot générique qui répond à côté.",
    },
    {
      q: "Comment marche le pointage selfie + polo ?",
      a: "Le salarié pointe depuis son téléphone — il prend un selfie en tenue de ton établissement. L'IA vérifie qu'il est bien sur place + en uniforme. Plus de pointage à distance, plus d'heures sup fictives. Les tâches assignées doivent aussi être validées par photo une fois terminées.",
    },
    {
      q: "L'inventaire fonctionne vraiment par photo ?",
      a: "Oui. Tu prends une photo de tes armoires/frigos, l'IA scanne et reconnaît les produits. Sinon ton salarié peut dicter à l'oral (vocal → texte) ou faire un inventaire manuel classique. Tu choisis — selon le profil et la situation.",
    },
    {
      q: 'Quelles données sont sync automatiquement ?',
      a: "Caisse, fournisseurs, factures, recettes, équipe. La plupart des connexions se font via OCR + IA — tu scannes une fiche manuscrite, tu charges ton ticket d'achat, Léo recolle tout sur tes recettes. Aucun double encodage.",
    },
    {
      q: 'Je dois changer mon POS ou logiciel de caisse ?',
      a: "Non. Audit Resto se branche par-dessus tes outils existants (Tiller, Lightspeed, Zelty, Sumup, Combo, etc.). Tu gardes tes systèmes, on rapatrie les données.",
    },
    {
      q: 'Comment marche le module Objectifs & Primes ?',
      a: "Chaque salarié a un score calculé automatiquement selon : tâches validées (avec photo), CA généré sur son service, assiduité (pointages), respect du planning. Les primes par paliers de CA (150 / 200 / 250€) sont calculées et versées sans erreur.",
    },
    {
      q: 'Combien de restaurants par compte ?',
      a: "1 établissement = 229€/mois. À partir du 2e resto : +99€/mois par établissement supplémentaire (donc 2 restos = 328€/mois, 3 restos = 427€/mois). Tarif dégressif négociable pour les groupes 5+ établissements (nous contacter via le chat IA).",
    },
    {
      q: 'Engagement ?',
      a: "Zéro engagement + 15 jours d'essai gratuit. Tu testes l'app dans son intégralité pendant 15 jours sans débit, et tu peux résilier en 1 clic via Stripe avant la fin de l'essai si ça te plaît pas. Aucune justification à donner.",
    },
    {
      q: "Je suis déjà accompagné par Sabril (Refonte/Ouverture). C'est inclus ?",
      a: "Les clients Refonte (6 700€) bénéficient de 3 mois offerts. Les clients Ouverture (14 900€) bénéficient de 6 mois offerts. L'app est ensuite à 229€/mois pour pérenniser les acquis.",
    },
    {
      q: "Les acheteurs de l'ebook 47€ ont un avantage ?",
      a: "Oui. Les 100 premiers Early Adopters (47€ lifetime) ont un tarif locked à 47€ à vie sur l'app — tant que les places ne sont pas épuisées.",
    },
    {
      q: 'Mes données sont en sécurité ?',
      a: "Hébergement européen, chiffrement bout-en-bout, conformité RGPD. Personne ne lit tes chiffres sauf toi. Export complet possible à tout moment.",
    },
    {
      q: "Et si l'app ne me convient pas ?",
      a: "30 jours pour décider. Si dans les 30 premiers jours tu ne sens pas que ça change ton resto, on te rembourse — pas de question.",
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }} data-testid="app-sales-page">
      {/* ============================== POST-CHECKOUT SUCCESS POPUP (Apple-style light) ============================== */}
      {postCheckoutSuccess && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[300] flex items-center justify-center p-5"
          style={{ background: 'rgba(0, 0, 0, 0.45)', backdropFilter: 'blur(20px)' }}
          data-testid="post-checkout-success"
        >
          <motion.div
            initial={{ scale: 0.96, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="relative w-full max-w-[480px] overflow-hidden text-center"
            style={{
              background: '#FFFFFF',
              borderRadius: '28px',
              boxShadow: '0 32px 80px rgba(0, 0, 0, 0.18), 0 0 0 0.5px rgba(0, 0, 0, 0.05)',
            }}
          >
            {/* Top: brand mark centered */}
            <div className="pt-8 pb-2 flex justify-center">
              <Resto360Mark size={42} tone="light" />
            </div>

            {/* Body */}
            <div className="px-8 pb-7">
              {/* Apple-style green success checkmark */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 14, stiffness: 280, delay: 0.15 }}
                className="w-14 h-14 rounded-full mx-auto mt-4 mb-5 flex items-center justify-center"
                style={{ background: '#34C759' }}
              >
                <Check className="w-7 h-7 text-white" strokeWidth={3} />
              </motion.div>

              <h2
                className="leading-tight mb-2"
                style={{
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Manrope', sans-serif",
                  fontSize: 28,
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                  color: '#1D1D1F',
                }}
              >
                Merci{postCheckoutData?.firstName ? ` ${postCheckoutData.firstName}` : ''}
              </h2>
              <p
                className="leading-relaxed mb-1"
                style={{
                  fontSize: 15,
                  color: '#6E6E73',
                  letterSpacing: '-0.01em',
                }}
              >
                Paiement confirmé · 0 € débité aujourd'hui
              </p>
              <p
                className="leading-relaxed mb-7"
                style={{
                  fontSize: 15,
                  color: '#6E6E73',
                  letterSpacing: '-0.01em',
                }}
              >
                {postCheckoutData?.restaurantName ? (
                  <>Ton espace <strong style={{ color: '#1D1D1F' }}>{postCheckoutData.restaurantName}</strong> est prêt.</>
                ) : (
                  <>Ton essai de 15 jours a démarré.</>
                )}
              </p>

              {/* Mail box block — clean Apple card style */}
              <div
                className="text-left rounded-2xl p-4 mb-6"
                style={{ background: '#F5F5F7' }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.06)' }}
                  >
                    <Mail className="w-4 h-4" style={{ color: '#FF5A1F' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#1D1D1F', letterSpacing: '-0.01em' }}>
                      Va sur ta boîte mail
                    </p>
                    <p style={{ fontSize: 13, color: '#6E6E73', marginTop: 4, lineHeight: 1.45 }}>
                      Sophie t'a envoyé{postCheckoutData?.email ? <> un email à <strong style={{ color: '#1D1D1F' }}>{postCheckoutData.email}</strong></> : ' un email'} avec ton lien d'activation pour définir ton mot de passe.
                    </p>
                    <p style={{ fontSize: 12, color: '#86868B', marginTop: 6, fontStyle: 'italic' }}>
                      Pense à vérifier tes spams si tu ne le vois pas dans 1-2 min.
                    </p>
                  </div>
                </div>
              </div>

              {postCheckoutData?.loginUrl ? (
                <a
                  href={postCheckoutData.loginUrl}
                  className="inline-flex items-center justify-center gap-2 w-full"
                  style={{
                    background: '#1D1D1F',
                    color: '#FFFFFF',
                    padding: '14px 24px',
                    borderRadius: 14,
                    fontSize: 15,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    transition: '180ms cubic-bezier(0.32, 0.72, 0, 1)',
                  }}
                  data-testid="post-checkout-activate-link"
                >
                  Activer mon compte
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <div className="flex items-center justify-center gap-2 py-3" style={{ color: '#86868B', fontSize: 13 }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#FF5A1F' }} />
                  <span>Préparation de ton espace en cours…</span>
                </div>
              )}

              {/* ====== Resend email block ====== */}
              {(() => {
                const elapsedSec = emailSentAt ? Math.floor((nowTick - emailSentAt) / 1000) : 0;
                const canResend = elapsedSec >= 60;
                const remaining = Math.max(0, 60 - elapsedSec);
                const formatElapsed = () => {
                  if (elapsedSec < 60) return `il y a ${elapsedSec}s`;
                  const mins = Math.floor(elapsedSec / 60);
                  return `il y a ${mins} min`;
                };
                return (
                  <div className="mt-5 pt-5" style={{ borderTop: '0.5px solid rgba(0,0,0,0.08)' }}>
                    <p style={{ fontSize: 12, color: '#86868B', marginBottom: 10 }} data-testid="email-elapsed-counter">
                      Email envoyé {formatElapsed()}
                    </p>
                    {resendState.success ? (
                      <div
                        className="inline-flex items-center justify-center gap-1.5"
                        style={{ background: 'rgba(52, 199, 89, 0.10)', color: '#248A3D', padding: '8px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}
                        data-testid="resend-success-badge"
                      >
                        <Check className="w-3.5 h-3.5" strokeWidth={2.75} />
                        Mail renvoyé ! Vérifie ta boîte
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendEmail}
                        disabled={!canResend || resendState.loading}
                        className="inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                        style={{
                          background: canResend && !resendState.loading ? '#F5F5F7' : 'transparent',
                          color: canResend && !resendState.loading ? '#1D1D1F' : '#AEAEB2',
                          padding: '9px 16px',
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 500,
                          letterSpacing: '-0.01em',
                          transition: '180ms cubic-bezier(0.32, 0.72, 0, 1)',
                        }}
                        data-testid="resend-email-btn"
                      >
                        {resendState.loading ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Envoi en cours…
                          </>
                        ) : canResend ? (
                          <>
                            <Mail className="w-3.5 h-3.5" />
                            Je n'ai rien reçu — Renvoyer
                          </>
                        ) : (
                          <>Renvoyer dans {remaining}s</>
                        )}
                      </button>
                    )}
                    {resendState.error && (
                      <p className="mt-2" style={{ fontSize: 12, color: '#FF3B30' }} data-testid="resend-error">
                        {resendState.error}
                      </p>
                    )}
                  </div>
                );
              })()}

              <button
                onClick={() => setPostCheckoutSuccess(false)}
                className="block mx-auto mt-5"
                style={{ fontSize: 12, color: '#86868B' }}
                data-testid="post-checkout-close"
              >
                Fermer
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* ============================== WELCOME MODAL (Stripe redirect — legacy fallback) ============================== */}
      {welcome && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-5"
          style={{ backgroundColor: 'rgba(26,26,26,0.78)', backdropFilter: 'blur(8px)' }}
          onClick={() => setWelcome(null)}
          data-testid="welcome-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[520px] rounded-3xl p-7 sm:p-9 text-center"
            style={{ backgroundColor: theme.card, boxShadow: '0 30px 80px rgba(0,0,0,0.35)' }}
          >
            <div className="flex justify-center mb-5">
              <div
                className="w-24 h-24 rounded-full overflow-hidden"
                style={{ border: '4px solid #3B82F6', boxShadow: '0 10px 30px rgba(59,130,246,0.35)' }}
              >
                <img src="/agents/sophie.png" alt="Sophie" className="w-full h-full object-cover" />
              </div>
            </div>
            <p className="text-[11px] font-bold tracking-[2.5px] uppercase mb-2" style={{ color: '#3B82F6' }}>
              Sophie · Audit Resto IA
            </p>
            <h2 className="font-bebas text-4xl sm:text-5xl leading-tight mb-3" style={{ color: theme.text }}>
              BIENVENUE{welcome.firstName ? `, ${welcome.firstName.toUpperCase()}` : ''} 🎉
            </h2>
            <p className="text-sm sm:text-base leading-relaxed mb-6" style={{ color: theme.textMuted }}>
              Ton abonnement est <strong style={{ color: theme.text }}>actif</strong>. Je t'ai envoyé un email{welcome.email ? ` à ${welcome.email}` : ''} avec ton accès direct à l'app + le programme des 7 prochains jours.
            </p>
            <a
              href="https://app.audit-resto.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-extrabold text-white text-base shadow-xl"
              style={{ backgroundColor: theme.orange }}
              data-testid="welcome-cta-app-button"
            >
              Accéder à l'app
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="https://billing.stripe.com/p/login/28E5kw7M2fwMaT79BYg7e00"
              target="_blank"
              rel="noopener noreferrer"
              className="block mx-auto mt-3 text-xs sm:text-sm font-bold underline"
              style={{ color: theme.textMuted }}
              data-testid="welcome-portal-link"
            >
              Gérer mon abonnement · changer carte · facture · annuler
            </a>
            <button
              onClick={() => setWelcome(null)}
              className="block mx-auto mt-5 text-xs underline"
              style={{ color: theme.textLight }}
            >
              Fermer
            </button>
          </motion.div>
        </motion.div>
      )}

      {/* ============================== NAV ============================== */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-lg"
        style={{ backgroundColor: 'rgba(245,243,240,0.95)', borderBottom: `1px solid ${theme.border}` }}
      >
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-xs font-bold tracking-wider"
            style={{ color: theme.textMuted }}
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            ACCUEIL
          </Link>
          <Resto360Wordmark variant="nav" tone="light" />
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleSubscribe}
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: theme.orange }}
            data-testid="nav-subscribe-btn"
            title={`+${PRICE_PER_EXTRA_RESTO}€/mois par resto supplémentaire`}
          >
            {PRICE}€/mois
            <ArrowRight className="w-3.5 h-3.5" />
          </motion.button>
        </div>
      </nav>

      {/* ============================== HERO ============================== */}
      <section className="pt-24 sm:pt-36 pb-12 sm:pb-20 px-5 sm:px-8">
        <div className="max-w-[1100px] mx-auto">
          {/* === MOBILE-ONLY EDITORIAL HERO CARD with Sabril Pixar avatar === */}
          <div className="sm:hidden mb-6">
            <motion.div
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-3xl overflow-hidden mb-4"
              data-testid="app-hero-mobile-card"
              style={{
                background: 'linear-gradient(180deg, #0F0F12 0%, #2A1810 100%)',
                boxShadow: '0 24px 50px rgba(0,0,0,0.40), 0 0 0 1px rgba(240,90,26,0.30) inset',
                aspectRatio: '4 / 5',
              }}
            >
              {/* Animated mesh glow */}
              <motion.div
                className="absolute -top-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(240,90,26,0.30) 0%, transparent 60%)' }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.55, 0.85, 0.55] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 60%)' }}
                animate={{ scale: [1.05, 1, 1.05], opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              />

              {/* Top: Live badge */}
              <div className="absolute top-3.5 left-3.5 z-10 flex items-center gap-1.5 rounded-full py-1 px-2.5 bg-emerald-500/20 backdrop-blur-md border border-emerald-400/40">
                <motion.span
                  className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                  animate={{ opacity: [1, 0.35, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity }}
                />
                <span className="text-[9px] font-black uppercase tracking-wider text-emerald-300">v4.2 Live</span>
              </div>

              {/* Top-right: NEW badge */}
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: -6 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 280 }}
                className="absolute top-3.5 right-3.5 z-10 rounded-2xl px-2.5 py-1.5 flex flex-col items-center"
                style={{
                  background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)',
                  boxShadow: '0 10px 24px rgba(240,90,26,0.55), 0 0 0 2px rgba(255,255,255,0.18) inset',
                }}
              >
                <span className="font-bebas text-white leading-none" style={{ fontSize: 18 }}>229€</span>
                <span className="text-white/90 text-[8px] font-bold uppercase tracking-wider mt-0.5">/mois</span>
              </motion.div>

              {/* Pixar avatar centered top */}
              <div className="absolute top-12 left-0 right-0 flex justify-center z-[2]">
                <SabrilPixarAvatar size={96} glow />
              </div>

              {/* Headline */}
              <div className="absolute inset-x-0 top-[42%] z-[2] px-5 text-center">
                <div className="flex justify-center mb-2">
                  <Resto360Wordmark variant="nav" tone="dark" />
                </div>
                <p
                  className="text-[9px] font-medium tracking-[3px] uppercase mb-1.5"
                  style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#A1A4AB' }}
                >
                  · App OS · v4.2
                </p>
                <h1
                  className="font-bebas leading-[0.88] tracking-tight text-white mb-2"
                  style={{ fontSize: 'clamp(38px, 11vw, 56px)' }}
                >
                  ARRÊTE DE
                  <br />
                  <span
                    style={{
                      background: 'linear-gradient(135deg, #FBBF24 0%, #F05A1A 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >GÉRER.</span> COMMENCE
                  <br />
                  À <span style={{ color: '#FFFFFF' }}>DIRIGER</span>.
                </h1>
                <p className="text-white/70 text-[11px] leading-snug italic">
                  « Le seul outil qui te paye <strong className="text-emerald-300">plus</strong> qu'il ne te coûte. »
                </p>
              </div>

              {/* Bottom: stats row */}
              <div className="absolute bottom-0 left-0 right-0 p-4 z-[2]">
                <div className="grid grid-cols-3 gap-2 mb-2.5">
                  <div className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12">
                    <div className="font-bebas text-white text-xl leading-none">28</div>
                    <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">modules</div>
                  </div>
                  <div className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12">
                    <div className="font-bebas text-[#FBBF24] text-xl leading-none">7 IA</div>
                    <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">agents</div>
                  </div>
                  <div className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12">
                    <div className="font-bebas text-emerald-300 text-xl leading-none">+800€</div>
                    <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">/mois</div>
                  </div>
                </div>
                {/* Bottom dark gradient */}
              </div>
              <div
                className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
                style={{ background: 'linear-gradient(0deg, rgba(15,15,18,0.95) 0%, transparent 100%)' }}
              />
            </motion.div>

            {/* CTA primary */}
            <motion.button
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-7 py-4 rounded-2xl font-extrabold text-white text-base shadow-xl disabled:opacity-70"
              style={{ background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)' }}
              data-testid="hero-subscribe-btn-mobile"
            >
              {loading ? 'Redirection Stripe…' : `Essai gratuit 15j · 0€ aujourd'hui`}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="#modules"
              className="mt-2 w-full flex items-center justify-center gap-2 px-7 py-3 rounded-2xl font-bold text-sm border"
              style={{
                backgroundColor: theme.card,
                color: theme.text,
                borderColor: theme.border,
              }}
              data-testid="hero-modules-btn-mobile"
            >
              Voir les 28 modules ↓
            </a>

            {/* Trust strip mobile */}
            <div className="flex items-center justify-center gap-4 mt-3 text-[10px] flex-wrap" style={{ color: theme.textMuted }}>
              <span className="flex items-center gap-1"><Sparkles className="w-3 h-3 text-emerald-500" />15 jours gratuits</span>
              <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />Résiliable en 1 clic</span>
            </div>
          </div>

          {/* === DESKTOP HERO (original, untouched) === */}
          <div className="hidden sm:block">

          {/* PUNCHLINE — Audit Resto remplace ton manager */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-7 inline-flex flex-col items-start gap-2 rounded-2xl px-5 py-4 border max-w-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(240,90,26,0.08) 0%, rgba(34,197,94,0.08) 100%)',
              borderColor: 'rgba(240,90,26,0.25)',
            }}
            data-testid="hero-punchline"
          >
            <p className="font-bebas text-xl sm:text-2xl lg:text-3xl tracking-wide leading-tight" style={{ color: theme.text }}>
              « LE SEUL OUTIL QUI TE PAYE <span style={{ color: '#22C55E' }}>PLUS</span> QU'IL NE TE COÛTE. »
            </p>
            <p className="text-xs sm:text-sm font-medium leading-relaxed" style={{ color: theme.textMuted }}>
              <span className="text-[10px] uppercase tracking-widest font-black mr-1.5" style={{ color: theme.orange }}>Spoiler</span>
              la moyenne récupère <strong style={{ color: theme.text }}>800€/mois</strong> dès la 4ème semaine.
              <br />
              <span className="inline-flex items-center gap-1 mt-1.5 text-[11px] sm:text-xs">
                <span className="text-base">💡</span>
                <span><strong style={{ color: theme.orange }}>28 modules en 1 app</strong> · ton tout-en-un qui transforme le patron en chef d'orchestre — <em>plus besoin de manager intermédiaire</em></span>
              </span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[2px] uppercase px-3 py-1.5 rounded-full mb-6"
            style={{ backgroundColor: theme.orangeDim, color: theme.orange, border: `1px solid ${theme.orangeBorder}` }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Audit Resto v4.2 · 229€/mois · 15 jours d'essai gratuit · sans engagement
          </motion.div>

          {/* Trial badge — pulse animation to draw the eye */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.05 }}
            className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-black tracking-[2px] uppercase px-3 py-1.5 rounded-full mb-6 ml-2"
            style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              color: 'white',
              boxShadow: '0 6px 18px rgba(34,197,94,0.45)',
            }}
            data-testid="trial-badge-hero"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            15 jours gratuits · 0€ aujourd'hui
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-bebas leading-[0.9] tracking-tight mb-6"
            style={{ color: theme.text, fontSize: 'clamp(54px,9vw,124px)' }}
          >
            ARRÊTE DE GÉRER
            <br /> TON RESTO.
            <br />
            COMMENCE À LE <span style={{ color: theme.orange }}>DIRIGER</span>.
          </motion.h1>

          {/* Sub-headline reinforcing "remplace ton manager" */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="max-w-[680px] text-sm sm:text-base font-bold tracking-wide uppercase mb-3"
            style={{ color: theme.orange, letterSpacing: '2px' }}
          >
            ⚡ 28 modules · 7 agents IA · ton manager, ton chef, ton compta, ton community manager — dans 1 app
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-[680px] text-base sm:text-xl leading-relaxed mb-10"
            style={{ color: theme.textMuted }}
          >
            <strong style={{ color: theme.text }}>Tes finances, ton équipe, tes recettes, tes réseaux, tes livraisons</strong> — pilotés depuis un seul écran.
            <strong style={{ color: theme.text }}> L'outil qui transforme le patron en chef d'orchestre — plus besoin d'intermédiaire.</strong>
          </motion.p>

          {/* Hero CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3 mb-10"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSubscribe}
              disabled={loading}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-7 py-5 rounded-2xl font-extrabold text-white text-base sm:text-lg shadow-xl disabled:opacity-70"
              style={{ backgroundColor: theme.orange, minWidth: 280 }}
              data-testid="hero-subscribe-btn"
            >
              {loading ? 'Redirection Stripe…' : `Démarrer maintenant — ${PRICE}€/mois`}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
            <a
              href="#modules"
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-7 py-5 rounded-2xl font-extrabold text-base sm:text-lg"
              style={{
                backgroundColor: theme.card,
                color: theme.text,
                border: `2px solid ${theme.border}`,
              }}
              data-testid="hero-modules-btn"
            >
              Voir les 28 modules
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs"
            style={{ color: theme.textMuted }}
          >
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" style={{ color: '#22C55E' }} />
              15 jours d'essai gratuit
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
              Sans engagement · résiliable en 1 clic
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
              RGPD · hébergement EU
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-4 h-4" style={{ color: '#22C55E' }} />
              Mises à jour incluses
            </span>
          </motion.div>
          </div>
          {/* /Desktop hero wrapper */}
        </div>
      </section>

      {/* ============================== APP DEMO VIDEO — 60s motion design ============================== */}
      <section className="py-12 sm:py-16 px-5 sm:px-8 border-t" style={{ borderColor: theme.border, background: '#0a0505' }} data-testid="app-demo-video-section">
        <div className="max-w-[1100px] mx-auto">
          <p className="text-[10px] sm:text-xs font-black tracking-[3px] uppercase mb-3 text-center" style={{ color: '#F05A1A' }}>
            ⚡ 60 secondes · De cuistot à PDG
          </p>
          <h2 className="font-bebas leading-[0.95] tracking-tight text-white text-center mb-3" style={{ fontSize: 'clamp(36px, 6vw, 64px)' }}>
            COMMENT 360 RESTO TRANSFORME<br />
            TON RESTO EN <span style={{ color: '#F05A1A' }}>MARQUE INDUSTRIELLE.</span>
          </h2>
          <p className="text-white/55 text-center max-w-xl mx-auto text-sm sm:text-base mb-8">
            Pilotable. Autonome. Scalable. Touche un chapitre en bas de la vidéo pour aller direct au feature qui t'intrigue.
          </p>
          <AppDemoVideo />

          {/* iOS Home Screen-style grid of all features inside the app */}
          <AppFeaturesGrid />
        </div>
      </section>

      {/* ============================== REAL DASHBOARD SCREENSHOT ============================== */}
      <RealDashboardShowcase />

      {/* ============================== ROI CALCULATOR ============================== */}
      <RoiCalculator onSubscribe={handleSubscribe} />

      {/* ============================== CE QUE TU GAGNES, TOI · Synthèse Patron ============================== */}
      <section className="py-16 sm:py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border, background: 'linear-gradient(180deg, #faf6f0 0%, #f5efe5 100%)' }} data-testid="patron-benefits-section">
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>Ce que tu gagnes, toi, en tant que patron</SectionTag>
          <SectionTitle className="mb-3">
            360 RESTO = <span style={{ color: theme.orange }}>TON MEILLEUR MANAGER</span>,<br />
            EN MOINS CHER.
          </SectionTitle>
          <p className="text-base sm:text-lg max-w-xl mb-12" style={{ color: theme.textMuted }}>
            Pas un dashboard de plus. Un outil qui <strong style={{ color: theme.text }}>récupère du cash que tu perdais</strong> et qui <strong style={{ color: theme.text }}>te rend du temps</strong> — chaque semaine.
          </p>

          {/* Synthèse — 6 metrics bento */}
          <div
            className="rounded-3xl p-6 sm:p-9 mb-14"
            style={{
              background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[
                { ic: '💰', val: '+1 800€', unit: '/an', label: 'récupérés (litiges Uber / Deliveroo)', color: '#22C55E' },
                { ic: '💰', val: '+1 500€', unit: '/recrue', label: 'économisés (formation auto)', color: '#22C55E' },
                { ic: '⏰', val: '−8h', unit: '/semaine', label: 'de management en moins', color: '#06B6D4' },
                { ic: '📉', val: '−40%', unit: '', label: 'de turnover équipe', color: '#06B6D4' },
                { ic: '✅', val: '100%', unit: '', label: 'compliance HACCP (certificats officiels)', color: '#F59E0B' },
                { ic: '😴', val: '+0', unit: '', label: 'nuit blanche à se demander "on a fait les contrôles ?"', color: '#F59E0B' },
              ].map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07 }}
                  className="rounded-2xl p-4 sm:p-5 border"
                  style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}
                >
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-2xl" aria-hidden>{m.ic}</span>
                    <span className="font-bebas text-3xl sm:text-4xl tabular-nums leading-none" style={{ color: m.color }}>
                      {m.val}
                    </span>
                    {m.unit && <span className="text-xs sm:text-sm font-bold" style={{ color: 'rgba(255,255,255,0.55)' }}>{m.unit}</span>}
                  </div>
                  <p className="text-[12px] sm:text-[13px] leading-snug" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    {m.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 3 cartes bénéfices patron */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                emoji: '📦',
                tag: 'Stop aux litiges',
                title: 'Uber Eats / Deliveroo — ENFIN tu as la preuve',
                accent: '#22C55E',
                bullets: [
                  { ic: '💰', txt: 'Récupère 150 à 300€/mois que tu perdais en litiges (sauces oubliées, plat manquant, ticket pas dans le sac).' },
                  { ic: '🛡️', txt: 'Photo + checklist horodatée = remboursement quasi systématique sur les 25€ de réclamation client.' },
                  { ic: '📊', txt: 'Tu vois en un coup d\'œil quelle plateforme te fait perdre le plus → tu négocies tes commissions ou tu déréférences.' },
                  { ic: '😴', txt: 'Ton équipe contrôle chaque commande sans que tu aies à le demander — c\'est devenu un réflexe gamifié (scan ticket en 5s, photo, fini).' },
                ],
                proof: 'Résultat : un resto qui fait 200 commandes Uber/mois récupère en moyenne ',
                proofVal: '1 800€/an',
                proofRest: ' de litiges qu\'il acceptait avant en se disant "bah c\'est la livraison".',
              },
              {
                emoji: '🏆',
                tag: 'Performance équipe',
                title: 'Tes employés te coûtent moins, te rapportent plus',
                accent: '#F59E0B',
                bullets: [
                  { ic: '💰', txt: 'Réduis ton turnover de 40% : un employé qui sait comment toucher 200€ de prime ce mois ne va pas chez le concurrent pour 50€ de plus.' },
                  { ic: '⏰', txt: '2-3h économisées par mois : le système calcule les primes à partir des vrais KPIs, plus de calcul manuel.' },
                  { ic: '🎯', txt: 'Tes salariés se challengent entre eux sans toi : classement live, top 3 boosté, podium dans le feed équipe.' },
                  { ic: '🧠', txt: 'Tu identifies tes vrais talents en 30 jours : qui mérite une augmentation, qui plombe l\'équipe, qui passe chef de rang. Que des chiffres.' },
                  { ic: '😌', txt: 'Fini les conflits "il a touché plus que moi" : tout le monde voit le même barème, temps réel, transparent.' },
                ],
                proof: 'Résultat : sur 8 salariés, un mois de prime mal calculée = un démissionnaire potentiel. Audit Resto rend la prime visible chaque jour → tes meilleurs éléments ',
                proofVal: 'restent',
                proofRest: ', et bossent comme s\'ils étaient associés.',
              },
              {
                emoji: '🎮',
                tag: 'Formation accélérée',
                title: 'Forme tes nouveaux en 15 jours, plus en 2 mois',
                accent: '#8B5CF6',
                bullets: [
                  { ic: '💰', txt: 'Économise 1 500€ par recrue : plus besoin que ton chef/manager passe 3 semaines à former le nouveau — il se forme le soir, sur son téléphone.' },
                  { ic: '⏰', txt: 'Récupère 8h/semaine que tu passais à expliquer, répéter, corriger les mêmes erreurs.' },
                  { ic: '🎓', txt: 'Certificats HACCP officiels (PDF + QR code de vérification, au nom de ton resto). En cas de contrôle DDPP, tu sors tes certificats = compliance prouvée.' },
                  { ic: '🚀', txt: 'Tes salariés ne quittent plus à 6 mois : niveaux, badges, podium → progression visible, ils évoluent au lieu de juste bosser.' },
                  { ic: '👑', txt: 'Toi-même tu te formes : Module Patron J30 — 9 modules stratégiques (Vision, Food Cost, Prime Cost, EBE, Seuil, Trésorerie, Marketing, KPIs) + certificat "Patron Niveau Avancé" pour LinkedIn.' },
                ],
                proof: 'Résultat : un patron qui ouvre un 2e resto (ZEBRA Marrakech → ZEBRA Casablanca) forme 100% de son équipe Casa ',
                proofVal: 'à distance',
                proofRest: ', sans déménager. Avant : 3 semaines sur place obligatoires.',
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="rounded-3xl p-6 sm:p-7 border bg-white relative overflow-hidden"
                style={{
                  borderColor: 'rgba(26,26,26,0.08)',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
                }}
                data-testid={`patron-benefit-card-${i}`}
              >
                {/* Accent ribbon */}
                <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: card.accent }} />

                <div className="flex items-start gap-3 mb-1">
                  <span className="text-4xl sm:text-5xl flex-shrink-0" aria-hidden>{card.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-black tracking-widest uppercase mb-1" style={{ color: card.accent }}>
                      {card.tag}
                    </p>
                    <h3 className="font-bebas text-2xl sm:text-3xl leading-tight tracking-tight" style={{ color: theme.text }}>
                      {card.title}
                    </h3>
                  </div>
                </div>

                <ul className="space-y-3 mt-5">
                  {card.bullets.map((b, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <span className="text-base flex-shrink-0 leading-snug" aria-hidden>{b.ic}</span>
                      <span className="text-[13px] sm:text-sm leading-relaxed" style={{ color: theme.textMuted }}>
                        {b.txt}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Proof line */}
                <div
                  className="mt-6 pt-5 border-t text-xs sm:text-[13px] leading-relaxed italic"
                  style={{ borderColor: 'rgba(26,26,26,0.08)', color: theme.textMuted }}
                >
                  <span className="not-italic">{card.proof}</span>
                  <strong className="not-italic" style={{ color: card.accent }}>{card.proofVal}</strong>
                  <span className="not-italic">{card.proofRest}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== APP PREVIEW STRIP ============================== */}
      <section className="py-16 px-5 sm:px-8" style={{ backgroundColor: '#1A1A1A' }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>L'app en vrai · v4.2 en production</SectionTag>
          <SectionTitle className="mb-10" >
            <span style={{ color: '#fff' }}>UNE INTERFACE.</span>
            <br />
            <span style={{ color: theme.orange }}>TOUS TES SUPER-POUVOIRS.</span>
          </SectionTitle>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl overflow-hidden"
            style={{
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
            }}
            data-testid="app-hero-screenshot"
          >
            <img src={SHOT_RECETTES} alt="Audit Resto v4.2 — module Recettes & Coûts" className="w-full h-auto block" />
          </motion.div>

          {/* Stats below screenshot */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {[
              { val: 28, suf: '', label: 'Modules connectés' },
              { val: 6, suf: '', label: 'Hubs métier' },
              { val: 80, suf: '%', label: 'De temps de gestion en moins' },
              { val: 5000, suf: '€', label: 'Économies moyennes / mois' },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl p-5 text-center"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Counter
                  to={s.val}
                  suffix={s.suf}
                  className="font-bebas text-4xl sm:text-5xl block leading-none"
                  style={{ color: theme.orange }}
                />
                <span className="text-[11px] mt-2 inline-block tracking-wider uppercase" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {s.label}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== LIVE DASHBOARD MOCKUPS ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border, backgroundColor: '#FAF6F0' }}>
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <SectionTag>Aperçu en temps réel · 6 hubs · 1 exemple par module</SectionTag>
              <SectionTitle>
                TON TABLEAU DE BORD <span style={{ color: theme.orange }}>EN DIRECT</span>.
              </SectionTitle>
            </div>
            <motion.span
              key={`updated-${liveTick}`}
              initial={{ opacity: 0.6 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[1.5px] uppercase px-3 py-1.5 rounded-full self-start sm:self-end"
              style={{ backgroundColor: theme.card, color: theme.textMuted, border: `1px solid ${theme.border}` }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#22C55E' }} />
              Mis à jour {(liveTick * 4) % 60 < 4 ? 'à l\'instant' : `il y a ${(liveTick * 4) % 60}s`}
            </motion.span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {/* RENTABILITÉ EN DIRECT */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className="rounded-3xl p-5 sm:p-6 text-white"
              style={{ background: 'linear-gradient(135deg,#16A34A 0%,#15803D 100%)', boxShadow: '0 18px 40px rgba(22,163,74,0.25)' }}
              data-testid="mock-rentabilite"
            >
              <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] font-bold tracking-[2px] uppercase opacity-80">Rentabilité · Mai 2026</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>EXCELLENT</span>
              </div>
              <div className="text-center mb-5">
                <p className="text-[11px] opacity-80 mb-1">Prime Cost</p>
                <p className="font-bebas leading-none" style={{ fontSize: 'clamp(56px,12vw,84px)' }}>58.4<span className="text-3xl">%</span></p>
                <p className="text-[11px] opacity-70 mt-1">Food cost + Masse salariale</p>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.14)' }}>
                  <p className="font-bebas text-3xl leading-none">27.8<span className="text-base">%</span></p>
                  <p className="text-[10px] opacity-80 mt-1 tracking-wider uppercase">Food cost</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.14)' }}>
                  <p className="font-bebas text-3xl leading-none">30.6<span className="text-base">%</span></p>
                  <p className="text-[10px] opacity-80 mt-1 tracking-wider uppercase">Labor cost</p>
                </div>
              </div>
              <div className="rounded-xl p-3 flex items-center justify-between" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                <div>
                  <p className="text-[11px] opacity-80">CA Mensuel</p>
                  <p className="font-bebas text-2xl leading-none">47 290 €</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] opacity-80">Objectif</p>
                  <p className="font-bebas text-2xl leading-none">50 000 €</p>
                </div>
              </div>
            </motion.div>

            {/* PLANNING ÉQUIPE */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="rounded-3xl p-5 sm:p-6"
              style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, boxShadow: theme.shadow }}
              data-testid="mock-planning"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase" style={{ color: theme.textMuted }}>Planning · Semaine 20</p>
                  <h3 className="font-bebas text-2xl leading-none mt-1" style={{ color: theme.text }}>12 SHIFTS · 86H</h3>
                </div>
                <span
                  className="text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1"
                  style={{ backgroundColor: '#8B5CF615', color: '#8B5CF6' }}
                >
                  <Sparkles className="w-3 h-3" />
                  IA Sophie
                </span>
              </div>
              <div className="space-y-2">
                {[
                  { name: 'Marie L.', role: 'Cheffe', hours: '11h–15h · 19h–23h', color: '#F05A1A', initials: 'ML' },
                  { name: 'Lucas P.', role: 'Commis', hours: '10h–14h · 18h–22h', color: '#3B82F6', initials: 'LP' },
                  { name: 'Sophie M.', role: 'Salle', hours: '11h–15h · 19h–23h', color: '#EC4899', initials: 'SM' },
                  { name: 'Adam K.', role: 'Plongeur', hours: '12h–15h · 20h–23h', color: '#22C55E', initials: 'AK' },
                ].map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(26,26,26,0.03)' }}>
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs text-white flex-shrink-0"
                      style={{ backgroundColor: p.color }}
                    >
                      {p.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold leading-tight" style={{ color: theme.text }}>{p.name}</p>
                      <p className="text-[11px]" style={{ color: theme.textMuted }}>{p.role} · {p.hours}</p>
                    </div>
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#22C55E' }} strokeWidth={3} />
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 flex items-center justify-between text-[11px]" style={{ borderTop: `1px solid ${theme.border}`, color: theme.textMuted }}>
                <span>Coût estimé semaine</span>
                <span className="font-bebas text-lg" style={{ color: theme.text }}>1 247 €</span>
              </div>
            </motion.div>

            {/* RECETTES & FOOD COST */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.16 }}
              className="rounded-3xl p-5 sm:p-6"
              style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, boxShadow: theme.shadow }}
              data-testid="mock-recettes"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase" style={{ color: theme.textMuted }}>Recettes · 34 fiches</p>
                  <h3 className="font-bebas text-2xl leading-none mt-1" style={{ color: theme.text }}>FOOD COST · TOP 4</h3>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: `${theme.orange}15`, color: theme.orange }}>
                  <ChefHat className="w-3 h-3" />
                  Léo IA
                </span>
              </div>
              <div className="space-y-2.5">
                {[
                  { name: 'Avo Bali', price: '13.90€', fc: 24.1, status: 'good' },
                  { name: 'Tartare de saumon', price: '16.50€', fc: 28.4, status: 'good' },
                  { name: 'Burger maison', price: '14.00€', fc: 32.7, status: 'warn' },
                  { name: 'Risotto truffe', price: '18.90€', fc: 41.2, status: 'bad' },
                ].map((r, i) => {
                  const c = r.status === 'good' ? '#22C55E' : r.status === 'warn' ? '#F59E0B' : '#EF4444';
                  return (
                    <div key={i} className="flex items-center justify-between gap-3 py-2" style={{ borderBottom: i < 3 ? `1px solid ${theme.border}` : 'none' }}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold truncate" style={{ color: theme.text }}>{r.name}</p>
                        <p className="text-[11px]" style={{ color: theme.textMuted }}>{r.price}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(26,26,26,0.06)' }}>
                          <div className="h-full rounded-full" style={{ backgroundColor: c, width: `${Math.min(r.fc * 2, 100)}%` }} />
                        </div>
                        <span className="font-bebas text-lg tabular-nums leading-none" style={{ color: c, minWidth: 48, textAlign: 'right' }}>
                          {r.fc.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 flex items-center justify-between text-[11px]" style={{ borderTop: `1px solid ${theme.border}`, color: theme.textMuted }}>
                <span>Food cost moyen</span>
                <span className="font-bebas text-lg" style={{ color: '#22C55E' }}>27.8%</span>
              </div>
            </motion.div>

            {/* RÉSEAUX SOCIAUX HUGO */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.24 }}
              className="rounded-3xl p-5 sm:p-6 text-white"
              style={{ background: 'linear-gradient(135deg,#EC4899 0%,#8B5CF6 50%,#F05A1A 100%)', boxShadow: '0 18px 40px rgba(236,72,153,0.25)' }}
              data-testid="mock-social"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase opacity-90">Instagram · @maisonbensimon</p>
                  <h3 className="font-bebas text-2xl leading-none mt-1">RÉSEAUX SOCIAUX</h3>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}>
                  <Sparkles className="w-3 h-3" />
                  Clara IA
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { v: '4 280', l: 'Followers', plus: '+12%' },
                  { v: '184k', l: 'Portée 30j', plus: '+38%' },
                  { v: '5.8%', l: 'Engagement', plus: '+1.4pt' },
                ].map((s, i) => (
                  <div key={i} className="rounded-xl p-3 text-center" style={{ backgroundColor: 'rgba(0,0,0,0.18)' }}>
                    <p className="font-bebas text-2xl leading-none">{s.v}</p>
                    <p className="text-[10px] opacity-80 mt-1">{s.l}</p>
                    <p className="text-[10px] font-bold mt-0.5" style={{ color: '#86EFAC' }}>↑ {s.plus}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(0,0,0,0.22)' }}>
                <p className="text-[10px] font-bold tracking-wider uppercase opacity-80 mb-2">📅 Prochaine publication suggérée</p>
                <p className="text-sm font-bold leading-snug">"Le secret du risotto crémeux en 30s 🍚✨"</p>
                <p className="text-[11px] opacity-80 mt-1">TikTok · Vendredi 19h · Format viral coulisse cuisine</p>
              </div>
            </motion.div>

            {/* POINTAGE ÉQUIPE */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.32 }}
              className="rounded-3xl p-5 sm:p-6 md:col-span-2 relative overflow-hidden"
              style={{ backgroundColor: theme.text, color: '#fff', boxShadow: '0 18px 40px rgba(0,0,0,0.18)' }}
              data-testid="mock-pointage"
            >
              {/* Live ping flash on each tick */}
              <motion.div
                key={`flash-${liveTick}`}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 1.6 }}
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(circle at top right, rgba(34,197,94,0.18), transparent 60%)' }}
              />
              <div className="flex items-center justify-between mb-5 flex-wrap gap-2 relative">
                <div>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase" style={{ color: 'rgba(255,255,255,0.55)' }}>Pointage du jour · Vendredi 15 mai</p>
                  <h3 className="font-bebas text-2xl leading-none mt-1">ÉQUIPE EN SERVICE</h3>
                </div>
                <motion.span
                  key={`live-${liveTick}`}
                  initial={{ scale: 0.95 }}
                  animate={{ scale: [0.95, 1.06, 1] }}
                  transition={{ duration: 0.6 }}
                  className="text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-2"
                  style={{ backgroundColor: 'rgba(34,197,94,0.18)', color: '#86EFAC' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#22C55E' }} />
                  LIVE · 4/4 pointés
                </motion.span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 relative">
                {[
                  { name: 'Marie L.', role: 'Cheffe', baseMin: 58, color: '#F05A1A' },
                  { name: 'Lucas P.', role: 'Commis', baseMin: 62, color: '#3B82F6' },
                  { name: 'Sophie M.', role: 'Salle', baseMin: 64, color: '#EC4899' },
                  { name: 'Adam K.', role: 'Plongeur', baseMin: 115, color: '#22C55E' },
                ].map((p, i) => {
                  // base time 10:00 + baseMin + liveTick seconds (offset slightly per person)
                  const totalSec = p.baseMin * 60 + liveTick * 4 + i * 7;
                  const h = 10 + Math.floor(totalSec / 3600);
                  const m = Math.floor((totalSec % 3600) / 60);
                  const s = totalSec % 60;
                  const tt = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                  const justUpdated = liveTick > 0 && i === (liveTick % 4);
                  return (
                    <motion.div
                      key={i}
                      animate={justUpdated ? { borderColor: 'rgba(34,197,94,0.45)' } : { borderColor: 'rgba(255,255,255,0.08)' }}
                      transition={{ duration: 0.8 }}
                      className="rounded-xl p-3"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderStyle: 'solid' }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0" style={{ backgroundColor: p.color, color: '#fff' }}>
                          {p.name.split(' ').map(x => x[0]).join('')}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold leading-tight truncate">{p.name}</p>
                          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{p.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <motion.span
                          key={`t-${i}-${liveTick}`}
                          initial={{ opacity: justUpdated ? 0.4 : 1 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5 }}
                          className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-md"
                          style={{ backgroundColor: 'rgba(34,197,94,0.18)', color: '#86EFAC' }}
                        >
                          ⏱ {tt}
                        </motion.span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(34,197,94,0.18)', color: '#86EFAC' }}>📸</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ backgroundColor: 'rgba(34,197,94,0.18)', color: '#86EFAC' }}>👕</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 grid grid-cols-3 gap-3 text-center relative" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div>
                  <p className="font-bebas text-2xl leading-none" style={{ color: '#86EFAC' }}>0</p>
                  <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>Retards</p>
                </div>
                <div>
                  <motion.p
                    key={`hs-${liveTick}`}
                    initial={{ scale: 1.0 }}
                    animate={{ scale: [1.0, 1.08, 1.0] }}
                    transition={{ duration: 0.6 }}
                    className="font-bebas text-2xl leading-none tabular-nums"
                  >
                    +2h{String(12 + Math.floor(liveTick / 4)).padStart(2, '0')}
                  </motion.p>
                  <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>Heures sup détectées</p>
                </div>
                <div>
                  <motion.p
                    key={`m-${liveTick}`}
                    initial={{ scale: 1.0 }}
                    animate={{ scale: [1.0, 1.08, 1.0] }}
                    transition={{ duration: 0.6 }}
                    className="font-bebas text-2xl leading-none tabular-nums"
                    style={{ color: theme.orange }}
                  >
                    {(1320 + liveTick * 3).toLocaleString('fr-FR')} €
                  </motion.p>
                  <p className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>Masse jour (réel)</p>
                </div>
              </div>
            </motion.div>

            {/* FOURNISSEURS · Commande Hebdo */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="rounded-3xl p-5 sm:p-6"
              style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, boxShadow: theme.shadow }}
              data-testid="mock-fournisseurs"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase" style={{ color: theme.textMuted }}>Commande hebdo · Lundi 18 mai</p>
                  <h3 className="font-bebas text-2xl leading-none mt-1" style={{ color: theme.text }}>METRO · 18 PRODUITS</h3>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: '#8B5CF615', color: '#8B5CF6' }}>
                  <Package className="w-3 h-3" />
                  Auto
                </span>
              </div>
              <div className="space-y-2">
                {[
                  { name: 'Avocats Hass', qty: '6 kg', price: '38.40€', old: '42.60€' },
                  { name: 'Saumon frais Norvège', qty: '4 kg', price: '74.80€', old: null },
                  { name: 'Riz arborio', qty: '10 kg', price: '32.00€', old: '34.50€' },
                  { name: 'Tomates anciennes', qty: '8 kg', price: '29.60€', old: null },
                ].map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 py-2.5" style={{ borderBottom: i < 3 ? `1px solid ${theme.border}` : 'none' }}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold truncate" style={{ color: theme.text }}>{p.name}</p>
                      <p className="text-[11px]" style={{ color: theme.textMuted }}>{p.qty}</p>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      {p.old && (
                        <span className="text-[10px] line-through tabular-nums" style={{ color: theme.textLight }}>{p.old}</span>
                      )}
                      <span className="font-bebas text-lg tabular-nums leading-none" style={{ color: p.old ? '#22C55E' : theme.text }}>{p.price}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="mt-4 rounded-xl p-3 flex items-center justify-between"
                style={{ background: 'linear-gradient(135deg,#8B5CF615,#8B5CF608)', border: '1px solid #8B5CF625' }}
              >
                <div>
                  <p className="text-[10px] font-bold tracking-wider uppercase" style={{ color: '#8B5CF6' }}>📄 PDF prêt à envoyer</p>
                  <p className="text-[11px] mt-0.5" style={{ color: theme.textMuted }}>commande@metro.fr · auto à 09:00</p>
                </div>
                <span className="font-bebas text-xl tabular-nums" style={{ color: theme.text }}>347.80€</span>
              </div>
            </motion.div>

            {/* PERFORMANCE · Scoring Équipe */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.16 }}
              className="rounded-3xl p-5 sm:p-6 text-white"
              style={{ background: 'linear-gradient(135deg,#F59E0B 0%,#EA580C 100%)', boxShadow: '0 18px 40px rgba(245,158,11,0.25)' }}
              data-testid="mock-performance"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold tracking-[2px] uppercase opacity-80">Classement Mai · Primes auto</p>
                  <h3 className="font-bebas text-2xl leading-none mt-1">PERFORMANCE ÉQUIPE</h3>
                </div>
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: 'rgba(0,0,0,0.22)' }}>
                  <Trophy className="w-3 h-3" />
                  Manu IA
                </span>
              </div>
              <div className="space-y-2">
                {[
                  { rank: '🥇', name: 'Marie L.', role: 'Cheffe', score: 96, prime: '250€' },
                  { rank: '🥈', name: 'Sophie M.', role: 'Salle', score: 88, prime: '200€' },
                  { rank: '🥉', name: 'Lucas P.', role: 'Commis', score: 82, prime: '150€' },
                  { rank: ' 4 ', name: 'Adam K.', role: 'Plongeur', score: 71, prime: '—' },
                ].map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(0,0,0,0.16)' }}>
                    <div className="font-bebas text-xl w-8 text-center flex-shrink-0">{p.rank}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold leading-tight">{p.name}</p>
                      <p className="text-[10px] opacity-75">{p.role}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }}>
                        <div className="h-full rounded-full bg-white" style={{ width: `${p.score}%` }} />
                      </div>
                      <span className="font-bebas text-lg tabular-nums leading-none w-8 text-right">{p.score}</span>
                    </div>
                    <span className="font-bebas text-base tabular-nums leading-none w-12 text-right flex-shrink-0" style={{ color: p.prime === '—' ? 'rgba(255,255,255,0.45)' : '#FCD34D' }}>
                      {p.prime}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 grid grid-cols-3 gap-2 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.18)' }}>
                <div>
                  <p className="text-[10px] opacity-75">Tâches validées</p>
                  <p className="font-bebas text-xl">87%</p>
                </div>
                <div>
                  <p className="text-[10px] opacity-75">Assiduité</p>
                  <p className="font-bebas text-xl">100%</p>
                </div>
                <div>
                  <p className="text-[10px] opacity-75">Primes versées</p>
                  <p className="font-bebas text-xl">600€</p>
                </div>
              </div>
            </motion.div>
          </div>

          <p className="text-[11px] text-center mt-8 max-w-[640px] mx-auto" style={{ color: theme.textMuted }}>
            <strong style={{ color: theme.text }}>Données démo</strong> — l'app calcule tout ça en direct à partir de tes ventes, factures, recettes et pointages.
            Chaque widget est interactif dans l'app et adaptable à ton resto.
          </p>
        </div>
      </section>

      {/* ============================== PAIN SECTION ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>Aujourd'hui ton resto, c'est…</SectionTag>
          <SectionTitle className="mb-10">
            8 ABONNEMENTS DIFFÉRENTS.
            <br />
            <span style={{ color: theme.orange }}>AUCUN QUI SE PARLE.</span>
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-10">
            {[
              { name: 'POS / Caisse', cost: '180€/mois' },
              { name: 'Food cost manuel sur Excel', cost: '∞ heures' },
              { name: 'Planning équipe (Combo, Skello…)', cost: '90€/mois' },
              { name: 'HACCP papier ou Excel', cost: 'risque amende' },
              { name: 'Commandes fournisseurs au tél', cost: 'temps perdu' },
              { name: 'Stockage docs RH (Dropbox + email)', cost: '15€/mois' },
              { name: 'Réseaux sociaux (Buffer, Later…)', cost: '25€/mois' },
              { name: 'Coach business / consulting', cost: '500–2000€/mois' },
            ].map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="flex items-center justify-between gap-4 p-4 rounded-xl"
                style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}
              >
                <span className="flex items-center gap-3 text-sm" style={{ color: theme.text }}>
                  <X className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} strokeWidth={3} />
                  {it.name}
                </span>
                <span className="text-xs font-bold tabular-nums" style={{ color: theme.textMuted }}>{it.cost}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="rounded-3xl p-7 sm:p-9 text-center"
            style={{
              background: `linear-gradient(135deg, ${theme.orange} 0%, #D94D0F 100%)`,
              color: '#fff',
            }}
          >
            <p className="text-xs sm:text-sm font-bold tracking-[2px] uppercase mb-2 opacity-90">Total mensuel actuel</p>
            <p className="font-bebas leading-none mb-4" style={{ fontSize: 'clamp(56px,12vw,120px)' }}>
              800 — 2 500€<span className="text-3xl sm:text-5xl opacity-70">/mois</span>
            </p>
            <p className="text-sm sm:text-base max-w-lg mx-auto opacity-90">
              Et toujours <strong>aucune vue d'ensemble</strong>. Tu ouvres 6 onglets pour piloter une semaine.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================== SOLUTION ============================== */}
      <section className="py-20 px-5 sm:px-8" style={{ backgroundColor: '#FAF6F0' }}>
        <div className="max-w-[1100px] mx-auto text-center">
          <SectionTag>La solution</SectionTag>
          <SectionTitle className="mb-6">
            UNE APP. <span style={{ color: theme.orange }}>{PRICE}€/MOIS.</span>
            <br />
            TOUT CONNECTÉ.
          </SectionTitle>
          <p className="max-w-[640px] mx-auto text-base sm:text-lg leading-relaxed" style={{ color: theme.textMuted }}>
            Audit Resto remplace <strong style={{ color: theme.text }}>jusqu'à 8 outils</strong> que tu payes déjà séparément.
            Tu économises ~600€/mois. Tu gagnes 3-5h par semaine. Et surtout : tu vois enfin
            <strong style={{ color: theme.text }}> où part ton argent</strong>.
          </p>
        </div>
      </section>

      {/* ============================== KILLER FEATURES ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>3 features qui n'existent nulle part ailleurs</SectionTag>
          <SectionTitle className="mb-16">
            CE QUE LES <span style={{ color: theme.orange }}>AUTRES NE FONT PAS</span>.
          </SectionTitle>

          <div className="space-y-20">
            <KillerCard
              icon={ScanLine}
              badge="OCR & IA — Léo"
              title="Scanne une fiche manuscrite. L'app la transforme en fiche technique calibrée."
              desc="Tu prends en photo le carnet de ton chef — Léo (l'IA cuisine) lit, identifie les ingrédients, calcule le food cost au gramme près, et te sort une fiche prête à imprimer. Plus jamais d'Excel."
              image={SHOT_RECETTES}
              delay={0}
            />
            <KillerCard
              icon={Users}
              badge="Pointage Sécurisé · Sophie"
              title="Pointage selfie + polo. Fini les heures fantômes."
              desc="Tes salariés pointent depuis leur téléphone — selfie obligatoire en tenue de l'établissement. L'app vérifie automatiquement qu'ils sont bien sur place, en uniforme. Plus de pointage à distance, plus d'heures sup fictives. Et chaque tâche assignée doit être validée par photo une fois terminée."
              mock={<PointageMock />}
              reverse
              delay={0.1}
            />
            <KillerCard
              icon={MessageSquare}
              badge="Coach IA Sabril intégré"
              title="Pose une question. Sabril répond avec sa vraie méthode, basée sur 10 ans de terrain."
              desc={`"Mon food cost est à 38%, j'ai besoin d'embaucher un commis, qu'est-ce que je fais en premier ?" → Réponse en 3 secondes, contextuelle, basée sur tes vrais chiffres. Plus de blocage. Un mentor restaurateur 24/7 dans ta poche.`}
              mock={<CoachChatMock />}
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* ============================== AGENTS IA SECTION ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border, backgroundColor: '#FAF6F0' }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>7 agents IA spécialisés · 1 équipe digitale</SectionTag>
          <SectionTitle className="mb-4">
            CHAQUE MODULE A SON <span style={{ color: theme.orange }}>EXPERT IA</span>.
          </SectionTitle>
          <p className="max-w-[640px] text-sm sm:text-base mb-12" style={{ color: theme.textMuted }}>
            Pas un seul chatbot générique. <strong style={{ color: theme.text }}>Sept agents IA dédiés</strong>, chacun spécialisé sur un domaine.
            Ils bossent en parallèle pour toi — comme une équipe complète, sans charges sociales.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[
              { name: 'Clara', role: 'Réseaux Sociaux', img: '/agents/clara.png', color: '#EC4899', desc: 'Génère tes vidéos virales TikTok/Insta, écrit tes scripts, planifie ton calendrier éditorial.' },
              { name: 'Sophie', role: 'RH & Planning', img: '/agents/sophie.png', color: '#F97316', desc: 'Propose le planning optimal, assigne les tâches au bon profil, gère absences et contrats.' },
              { name: 'Léo', role: 'Cuisine & Recettes', img: '/agents/leo.png', color: '#22C55E', desc: 'Scanne tes fiches manuscrites, calibre tes plats au gramme, optimise ton food cost.' },
              { name: 'Manu', role: 'Recrutement & Évaluation', img: '/agents/manu.png', color: '#3B82F6', desc: 'Scanne les CV reçus, score chaque candidat, calcule les primes équipe au pourcentage près.' },
              { name: 'Théo', role: 'Finance & Pilotage', img: '/agents/theo.png', color: '#8B5CF6', desc: 'Pilote ta rentabilité en direct, prime cost et EBE temps réel, alerte sur les anomalies de marge.' },
              { name: 'Nora', role: 'Insights & Tendances 2026', img: '/agents/nora.png', color: '#06B6D4', desc: 'Analyse ton domaine, surveille les tendances locales, propose le menu gagnant adapté à ta zone.' },
              { name: 'Sabril (IA)', role: 'Coach Stratégique', img: '/agents/sabril.png', color: '#F05A1A', desc: 'Le mentor 24/7. Pose ta question business — réponse contextuelle basée sur 10 ans de terrain.' },
            ].map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: i * 0.07 }}
                className="rounded-2xl p-6 flex flex-col"
                style={{
                  backgroundColor: theme.card,
                  border: `1px solid ${theme.border}`,
                  boxShadow: theme.shadow,
                }}
                data-testid={`agent-card-${a.name.toLowerCase().replace(/[^a-z]/g, '')}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
                    style={{ border: `3px solid ${a.color}`, boxShadow: `0 6px 18px ${a.color}33` }}
                  >
                    <img
                      src={a.img}
                      alt={a.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="font-bebas text-3xl leading-none" style={{ color: theme.text }}>{a.name}</h3>
                    <p className="text-[11px] font-bold tracking-[1.5px] uppercase mt-1" style={{ color: a.color }}>
                      {a.role}
                    </p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: theme.textMuted }}>{a.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 rounded-2xl p-5 sm:p-6 text-center"
            style={{
              background: `linear-gradient(135deg, ${theme.text} 0%, #2A2A2A 100%)`,
              color: '#fff',
            }}
          >
            <p className="text-sm sm:text-base leading-relaxed">
              <strong>Équivalent d'une équipe de 6 personnes</strong> à temps plein, qui bossent 24/7, ne se plaignent jamais et coûtent <strong style={{ color: theme.orange }}>229€/mois au total</strong>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================== ALL HUBS ============================== */}
      <section id="modules" className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>Inventaire complet · l'impact chiffré de chaque module</SectionTag>
          <SectionTitle className="mb-4">
            6 HUBS. <span style={{ color: theme.orange }}>28 MODULES.</span>
            <br />
            UNE SEULE INTERFACE.
          </SectionTitle>
          <p className="max-w-[680px] text-sm sm:text-base mb-6" style={{ color: theme.textMuted }}>
            Chaque module est <strong style={{ color: theme.text }}>connecté aux autres</strong>. Tu modifies une recette → ton food cost se recalcule → ton inventaire s'ajuste → ta marge se met à jour. <strong style={{ color: theme.text }}>Magic.</strong>
          </p>

          {/* Legend — Loss vs Unlock */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-10 text-[11px] sm:text-xs">
            <span
              className="inline-flex items-center gap-1.5 font-bold px-2.5 py-1.5 rounded-lg"
              style={{ backgroundColor: 'rgba(239,68,68,0.10)', color: '#DC2626', border: '1px solid rgba(239,68,68,0.25)' }}
            >
              🩸 Sans ça
            </span>
            <span style={{ color: theme.textMuted }}>= ce que tu <strong style={{ color: theme.text }}>perds chaque mois</strong> sans le module</span>
            <span className="mx-1 text-[#1a1a1a]/20">·</span>
            <span
              className="inline-flex items-center gap-1.5 font-bold px-2.5 py-1.5 rounded-lg"
              style={{ backgroundColor: 'rgba(34,197,94,0.10)', color: '#059669', border: '1px solid rgba(34,197,94,0.25)' }}
            >
              🚀 Avec ça
            </span>
            <span style={{ color: theme.textMuted }}>= ce que tu <strong style={{ color: theme.text }}>débloques</strong> dès la 1ère semaine</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 items-start">
            {hubs.map((h, i) => (
              <HubCard key={h.title} {...h} delay={i * 0.06} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================== ROI BLOCK ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border, backgroundColor: '#1A1A1A' }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>Économies réelles · resto type 30 000€/mois CA</SectionTag>
          <SectionTitle className="mb-12" >
            <span style={{ color: '#fff' }}>229€/MOIS.</span>
            <br />
            <span style={{ color: theme.orange }}>5 000€ RÉCUPÉRÉS.</span>
          </SectionTitle>

          <div
            className="rounded-3xl p-6 sm:p-10 mb-8"
            style={{
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {[
              { icon: '📦', label: 'Litiges Uber Eats / Deliveroo récupérés', val: '+150€/mois', positive: true },
              { icon: '📉', label: 'Économies sur abonnements remplacés', val: '−600€/mois', positive: true },
              { icon: '🍳', label: 'Food cost optimisé (32% → 28%)', val: '+1 200€/mois', positive: true },
              { icon: '👥', label: 'Masse salariale ajustée (heures sup détectées)', val: '+1 800€/mois', positive: true },
              { icon: '📱', label: 'Réseaux sociaux automatisés (+15% CA)', val: '+4 500€/mois', positive: true },
              { icon: '⏱️', label: 'Temps de gestion économisé', val: '12h/semaine', positive: true },
              { icon: '💳', label: 'Investissement Audit Resto', val: '−229€/mois', positive: false },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex items-center justify-between gap-4 py-4 border-b"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }}
              >
                <span className="flex items-center gap-3 text-sm sm:text-base" style={{ color: 'rgba(255,255,255,0.85)' }}>
                  <span className="text-xl">{row.icon}</span>
                  {row.label}
                </span>
                <span
                  className="font-bebas text-2xl sm:text-3xl tabular-nums"
                  style={{ color: row.positive ? '#22C55E' : theme.orange }}
                >
                  {row.val}
                </span>
              </motion.div>
            ))}

            <div className="pt-6 mt-2 text-center">
              <p className="text-[11px] font-bold tracking-[2px] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Impact net mensuel
              </p>
              <p
                className="font-bebas leading-none"
                style={{ fontSize: 'clamp(56px,12vw,120px)', color: '#22C55E' }}
              >
                +7 151€<span className="text-3xl sm:text-4xl opacity-60">/mois</span>
              </p>
              <p className="text-sm mt-3" style={{ color: 'rgba(255,255,255,0.65)' }}>
                Soit un <strong style={{ color: '#fff' }}>ROI x20</strong> sur le tarif de l'app.
              </p>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full sm:w-auto block sm:inline-flex items-center justify-center gap-2 px-8 py-5 rounded-2xl font-extrabold text-white text-base sm:text-lg mx-auto disabled:opacity-70"
            style={{ backgroundColor: theme.orange }}
            data-testid="roi-subscribe-btn"
          >
            Récupérer mes 5 000€/mois — {PRICE}€/mois
            <ArrowRight className="w-5 h-5 inline ml-2" />
          </motion.button>
        </div>
      </section>

      {/* ============================== COMPARISON ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border }}>
        <div className="max-w-[1100px] mx-auto">
          <SectionTag>Comparaison</SectionTag>
          <SectionTitle className="mb-12">
            <span style={{ color: theme.orange }}>360 RESTO</span> VS LE RESTE.
          </SectionTitle>

          <div className="overflow-x-auto rounded-2xl" style={{ border: `1px solid ${theme.border}`, backgroundColor: theme.card }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'rgba(26,26,26,0.04)', borderBottom: `2px solid ${theme.border}` }}>
                  <th className="text-left p-4 font-extrabold" style={{ color: theme.text }}>Capacité</th>
                  <th className="text-center p-4 font-extrabold text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>POS classique</th>
                  <th className="text-center p-4 font-extrabold text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>Excel + Combo</th>
                  <th
                    className="text-center p-4 font-extrabold text-xs uppercase tracking-wider"
                    style={{ color: '#fff', backgroundColor: theme.orange, borderTopRightRadius: 12 }}
                  >
                    Audit Resto
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Food cost calculé automatiquement', false, false, true],
                  ['Scan fiches manuscrites (OCR)', false, false, true],
                  ['Pointage selfie + polo obligatoire', false, false, true],
                  ['Inventaire par photo des armoires', false, false, true],
                  ['Scan CV automatique + scoring', false, false, true],
                  ['7 agents IA spécialisés (Clara, Sophie, Léo, Manu, Théo, Sabril…)', false, false, true],
                  ['HACCP digitalisé', false, false, true],
                  ['Planning suggéré par IA', false, false, true],
                  ['Tableau de bord financier direct', false, false, true],
                  ['Générateur vidéos virales TikTok/Insta', false, false, true],
                  ["Prix mensuel total", "180€", "120€", `${PRICE}€`],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: i < 10 ? `1px solid ${theme.border}` : 'none' }}>
                    <td className="p-4 font-semibold" style={{ color: theme.text }}>{row[0]}</td>
                    {[1, 2, 3].map((idx) => (
                      <td key={idx} className="text-center p-4">
                        {typeof row[idx] === 'boolean' ? (
                          row[idx] ? (
                            <Check className="w-5 h-5 mx-auto" style={{ color: idx === 3 ? theme.orange : '#22C55E' }} strokeWidth={3} />
                          ) : (
                            <X className="w-5 h-5 mx-auto" style={{ color: '#EF4444' }} strokeWidth={3} />
                          )
                        ) : (
                          <span className="font-extrabold tabular-nums" style={{ color: idx === 3 ? theme.orange : theme.text }}>
                            {row[idx]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============================== FAQ ============================== */}
      <section className="py-20 px-5 sm:px-8 border-t" style={{ borderColor: theme.border }}>
        <div className="max-w-[820px] mx-auto">
          <SectionTag>FAQ</SectionTag>
          <SectionTitle className="mb-10">
            LES <span style={{ color: theme.orange }}>VRAIES QUESTIONS</span>.
          </SectionTitle>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <FaqItem
                key={i}
                index={i}
                q={f.q}
                a={f.a}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============== TENSION GATE — Moment de vérité avant Final CTA ============== */}
      <div className="px-5 sm:px-8 max-w-[1100px] mx-auto" data-testid="app-tension-gate-wrapper">
        <ResultsTensionGate variant="app_signup" testid="app-tension-gate" />
      </div>

      {/* ============================== FINAL CTA ============================== */}
      <section className="py-24 px-5 sm:px-8" style={{ backgroundColor: theme.text }}>
        <div className="max-w-[900px] mx-auto text-center">
          <SectionTag>Démarre aujourd'hui</SectionTag>
          <h2
            className="font-bebas leading-[0.95] mb-6"
            style={{ color: '#fff', fontSize: 'clamp(52px,10vw,108px)' }}
          >
            CHAQUE MOIS SANS L'APP =
            <br />
            <span style={{ color: theme.orange }}>7 000€ PERDUS</span>.
          </h2>
          <p className="text-base sm:text-lg max-w-[560px] mx-auto mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Tu peux continuer à galérer avec Excel. Ou tu peux passer 5 minutes pour activer ton compte
            et récupérer tes marges dès cette semaine. <strong style={{ color: '#fff' }}>15 jours d'essai gratuit, puis 229€/mois. Sans engagement. Résiliable en 1 clic.</strong>
          </p>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleSubscribe}
            disabled={loading}
            className="inline-flex items-center gap-3 px-9 py-5 rounded-2xl font-extrabold text-white text-base sm:text-xl shadow-2xl disabled:opacity-70"
            style={{ backgroundColor: theme.orange }}
            data-testid="final-subscribe-btn"
          >
            {loading ? 'Redirection Stripe…' : `Démarrer mon essai gratuit · 15 jours offerts`}
            <ArrowRight className="w-6 h-6" />
          </motion.button>
          <p className="text-xs mt-5" style={{ color: 'rgba(255,255,255,0.45)' }}>
            🔒 Paiement sécurisé Stripe · résiliable en 1 clic · TVA incluse
          </p>
        </div>
      </section>

      {/* ============================== FOOTER ============================== */}
      <footer className="py-8 px-5 text-center border-t" style={{ borderColor: theme.border }}>
        {/* Cross-sell: les 2 autres offres */}
        <div className="max-w-4xl mx-auto pb-8">
          <OffersQuickAccess
            variant="light"
            kicker="🎯 Autres offres"
            title="Tu veux aller plus loin ?"
            hideCurrentPage={true}
          />
        </div>
        <p className="text-[11px] tracking-wider" style={{ color: theme.textLight }}>
          AUDIT RESTO © 2026 · @sabril.j · <a href="/" className="underline">audit-resto.fr</a>
        </p>
        <p className="text-[11px] mt-2 tracking-wider" style={{ color: theme.textLight }}>
          Déjà client ?{' '}
          <a
            href="https://billing.stripe.com/p/login/28E5kw7M2fwMaT79BYg7e00"
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-bold"
            style={{ color: theme.text }}
            data-testid="footer-customer-portal-link"
          >
            Gérer mon abonnement
          </a>
        </p>
      </footer>

      {/* Pre-checkout modal — collects restaurant_name before launching Stripe checkout */}
      <AnimatePresence>
        {showRestaurantPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-4"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)' }}
            data-testid="restaurant-prompt-overlay"
            onClick={() => setShowRestaurantPrompt(false)}
          >
            <motion.form
              initial={{ y: 30, scale: 0.94, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 20, scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 240, damping: 22 }}
              className="relative w-full max-w-md rounded-3xl p-6 sm:p-7"
              style={{
                background: theme.cream || '#FAF7F2',
                color: theme.text || '#1A1A1A',
                boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
              }}
              data-testid="restaurant-prompt"
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => { e.preventDefault(); handleConfirmSubscribe(); }}
            >
              <button
                type="button"
                onClick={() => setShowRestaurantPrompt(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'rgba(0,0,0,0.06)' }}
                aria-label="Fermer"
                data-testid="restaurant-prompt-close"
              >
                ×
              </button>

              <p className="font-bebas text-3xl sm:text-4xl tracking-tight leading-none mb-2">
                ON CRÉE TON ESPACE.
              </p>
              <p className="text-sm leading-relaxed mb-5" style={{ color: theme.textMuted }}>
                Donne-moi le nom de ton restaurant — ton cockpit personnel sera créé direct après paiement.
              </p>

              <label htmlFor="restaurant-name-input" className="block text-[10px] uppercase tracking-[2.5px] font-bold mb-1.5" style={{ color: theme.textMuted }}>
                Nom du restaurant
              </label>
              <input
                id="restaurant-name-input"
                type="text"
                name="restaurant_name"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                placeholder="Ex : Le Comptoir Marais"
                maxLength={80}
                autoFocus
                disabled={subscribing}
                className="w-full rounded-xl px-4 py-3 text-base outline-none transition-all"
                style={{
                  background: '#fff',
                  border: `1.5px solid ${subscribeError ? '#dc2626' : 'rgba(0,0,0,0.10)'}`,
                  color: theme.text,
                  fontSize: '16px',
                }}
                data-testid="restaurant-prompt-input"
                autoComplete="organization"
              />

              {subscribeError && (
                <p className="text-[12px] mt-2 text-red-600" data-testid="restaurant-prompt-error">
                  {subscribeError}
                </p>
              )}

              <button
                type="submit"
                disabled={subscribing}
                className="w-full mt-5 py-3.5 rounded-xl font-extrabold text-white text-base flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                style={{
                  background: '#F05A1A',
                  boxShadow: '0 10px 24px rgba(240,90,26,0.40)',
                }}
                data-testid="restaurant-prompt-submit"
              >
                {subscribing ? 'Création de la session…' : "Démarrer mon essai gratuit · 0€ aujourd'hui"}
              </button>

              <p className="text-center text-[11px] mt-3" style={{ color: theme.textMuted }}>
                ✅ 15 jours gratuits · Puis 229€/mois · Résiliable en 1 clic à tout moment · CB demandée mais non débitée pendant l'essai
              </p>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <SabrilCloserBubble page="app" />
    </div>
  );
};

export default AppLaunchPage;
